import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { searchPlaces } from '../../services/api/geocodingApi';
import { convertManeuver } from '../../services/routing/mapboxDirections';
import { fetchAirQuality } from '../../services/api/airQualityApi';
import { useFeatureAccess, useSubscriptionStore } from '@/stores';
import { showUpgradeModal } from '@/subscription/components/UpgradeModal';

// ── Route Analysis Helpers ──────────────────────────────────

function getAqiInfo(aqi: number): { label: string; color: string } {
  if (aqi <= 50) return { label: 'Good', color: '#22c55e' };
  if (aqi <= 100) return { label: 'Moderate', color: '#eab308' };
  if (aqi <= 150) return { label: 'Sensitive', color: '#f97316' };
  if (aqi <= 200) return { label: 'Unhealthy', color: '#ef4444' };
  return { label: 'Hazardous', color: '#a855f7' };
}

/** Convert AQI to estimated PM2.5 µg/m³ (EPA breakpoints) */
function aqiToPm25(aqi: number): number {
  if (aqi <= 50)  return (aqi / 50) * 12;
  if (aqi <= 100) return 12 + ((aqi - 50) / 50) * 23.4;
  if (aqi <= 150) return 35.4 + ((aqi - 100) / 50) * 19.6;
  if (aqi <= 200) return 55 + ((aqi - 150) / 50) * 95;
  return 150;
}

/** Breathing exposure verdict for athlete route cards */
function getExposureInfo(mcg: number): { label: string; sublabel: string; color: string; bg: string } {
  if (mcg < 5)  return { label: 'Clean air', sublabel: 'Excellent for running', color: '#6b9f78', bg: 'rgba(107,159,120,0.12)' };
  if (mcg < 15) return { label: 'Moderate', sublabel: 'Acceptable conditions', color: '#b89f6a', bg: 'rgba(184,159,106,0.12)' };
  if (mcg < 30) return { label: 'Elevated', sublabel: 'Consider a mask', color: '#c97c4a', bg: 'rgba(201,124,74,0.12)' };
  return { label: 'High exposure', sublabel: 'Not ideal for running', color: '#a87272', bg: 'rgba(168,114,114,0.12)' };
}

function bearingToDirection(bearing: number): string {
  const dirs = ['North', 'Northeast', 'East', 'Southeast', 'South', 'Southwest', 'West', 'Northwest'];
  const index = Math.round(((bearing % 360) + 360) % 360 / 45) % 8;
  return dirs[index];
}

/** Sample N evenly-spaced points from a GeoJSON coordinate array */
function samplePoints(coords: [number, number][], n: number): { lat: number; lng: number }[] {
  if (coords.length === 0) return [];
  const step = Math.max(1, Math.floor(coords.length / n));
  const pts: { lat: number; lng: number }[] = [];
  for (let i = 0; i < coords.length && pts.length < n; i += step) {
    pts.push({ lat: coords[i][1], lng: coords[i][0] });
  }
  return pts;
}

/** Fetch elevation for points using Open-Meteo (free, fast, no key) */
async function fetchElevations(points: { lat: number; lng: number }[]): Promise<number[]> {
  if (points.length === 0) return [];
  const lats = points.map(p => p.lat.toFixed(4)).join(',');
  const lngs = points.map(p => p.lng.toFixed(4)).join(',');
  try {
    const resp = await fetch(`https://api.open-meteo.com/v1/elevation?latitude=${lats}&longitude=${lngs}`);
    const data = await resp.json();
    return data.elevation || [];
  } catch {
    return [];
  }
}

/** Calculate elevation gain/loss from elevation profile */
function calcElevation(elevations: number[]): { gain: number; loss: number } {
  let gain = 0, loss = 0;
  for (let i = 1; i < elevations.length; i++) {
    const diff = elevations[i] - elevations[i - 1];
    if (diff > 0) gain += diff;
    else loss += Math.abs(diff);
  }
  return { gain: Math.round(gain), loss: Math.round(loss) };
}

/**
 * Fetch shade score for a route via OSM Overpass API.
 * Counts tree nodes + park/forest area polygons along sampled route points.
 * Returns 0–100 (higher = shadier).
 */
async function fetchShadeScore(points: { lat: number; lng: number }[]): Promise<number> {
  if (points.length === 0) return 0;
  // Build a bounding box around the route points
  const lats = points.map(p => p.lat);
  const lngs = points.map(p => p.lng);
  const south = Math.min(...lats) - 0.003;
  const north = Math.max(...lats) + 0.003;
  const west  = Math.min(...lngs) - 0.003;
  const east  = Math.max(...lngs) + 0.003;

  // Overpass query: trees, forests, parks within the bounding box
  const query = `[out:json][timeout:10];
(
  node["natural"="tree"](${south},${west},${north},${east});
  way["landuse"="forest"](${south},${west},${north},${east});
  way["leisure"="park"](${south},${west},${north},${east});
  way["natural"="wood"](${south},${west},${north},${east});
);
out count;`;

  try {
    const resp = await fetch('https://overpass-api.de/api/interpreter', {
      method: 'POST',
      body: `data=${encodeURIComponent(query)}`,
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    });
    const data = await resp.json();
    const total = data?.elements?.[0]?.tags?.total ?? 0;
    // Normalise: 0 features = 0, ~200+ features = 100
    return Math.min(100, Math.round((total / 200) * 100));
  } catch {
    return 0;
  }
}

/** Fetch weather (humidity + temperature) at a specific point via Open-Meteo */
async function fetchRouteWeather(lat: number, lng: number): Promise<{ humidity: number; temperature: number } | null> {
  try {
    const url = `https://api.open-meteo.com/v1/forecast?latitude=${lat.toFixed(4)}&longitude=${lng.toFixed(4)}&current=relative_humidity_2m,temperature_2m&forecast_days=1`;
    const resp = await fetch(url);
    const data = await resp.json();
    return {
      humidity: data.current?.relative_humidity_2m ?? 70,
      temperature: data.current?.temperature_2m ?? 28,
    };
  } catch {
    return null;
  }
}

/** Analyze road types from Mapbox step instructions */
function analyzeRoadTypes(steps: any[]): { quietPct: number; breakdown: string } {
  if (!steps?.length) return { quietPct: 50, breakdown: 'Mixed roads' };
  let quietDist = 0, mainDist = 0, parkDist = 0, totalDist = 0;
  const parkPatterns = /\b(park|taman|garden|trail|path|lakeside|waterfront|boardwalk|promenade)\b/i;
  const mainPatterns = /\b(highway|expressway|motorway|jalan besar|main|boulevard|avenue|lebuhraya)\b/i;
  for (const step of steps) {
    const d = step.distance || 0;
    totalDist += d;
    const name = (step.name || '') + ' ' + (step.instruction || '');
    if (parkPatterns.test(name)) parkDist += d;
    else if (mainPatterns.test(name)) mainDist += d;
    else quietDist += d;
  }
  if (totalDist === 0) return { quietPct: 50, breakdown: 'Mixed roads' };
  const quietPct = Math.round(((quietDist + parkDist) / totalDist) * 100);
  const parkPct = Math.round((parkDist / totalDist) * 100);
  const mainPct = Math.round((mainDist / totalDist) * 100);
  let breakdown = 'Mixed roads';
  if (parkPct > 40) breakdown = 'Mostly parks & trails';
  else if (quietPct > 70) breakdown = 'Mostly quiet roads';
  else if (mainPct > 50) breakdown = 'Along main roads';
  else if (parkPct > 15) breakdown = 'Parks + residential';
  else breakdown = 'Residential streets';
  return { quietPct, breakdown };
}

/** Analyze a single Mapbox route and return metrics */
async function analyzeRoute(
  routeData: any,
  bearing: number,
  isRunning: boolean,
  paceMinPerKm: number
): Promise<RouteMetrics> {
  const route = routeData.routes[0];
  const coords: [number, number][] = route.geometry?.coordinates || [];
  const distKm = route.distance / 1000;
  const durationSec = isRunning ? distKm * paceMinPerKm * 60 : route.duration;

  // Sample points for AQI, elevation, shade, and weather
  const elevPoints = samplePoints(coords, 15);
  const shadePoints = samplePoints(coords, 8);
  const aqiPoint = samplePoints(coords, 1)[0]; // midpoint for AQI + weather

  // Fetch AQI, elevation, shade, and per-route weather in parallel
  const [aqiData, elevations, shadeScore, routeWeather] = await Promise.all([
    aqiPoint ? fetchAirQuality(aqiPoint.lat, aqiPoint.lng).catch(() => null) : null,
    fetchElevations(elevPoints),
    fetchShadeScore(shadePoints).catch(() => 0),
    aqiPoint ? fetchRouteWeather(aqiPoint.lat, aqiPoint.lng).catch(() => null) : null,
  ]);

  // AQI
  const aqi = aqiData?.aqi ?? 50;
  const { label: aqiLabel, color: aqiColor } = getAqiInfo(aqi);

  // Elevation
  const { gain: elevationGain, loss: elevationLoss } = calcElevation(elevations);

  // Road types
  const steps = route.legs?.flatMap((l: any) => l.steps || []) || [];
  const { quietPct: quietRoadPct, breakdown: roadBreakdown } = analyzeRoadTypes(steps);

  // Pace
  const paceTotal = distKm > 0 ? durationSec / distKm : paceMinPerKm * 60;
  const paceMin = Math.floor(paceTotal / 60);
  const paceSec = Math.round(paceTotal % 60);
  const pacePerKm = `${paceMin}:${String(paceSec).padStart(2, '0')}/km`;

  // Direction
  const direction = bearingToDirection(bearing);

  // Breathing exposure: PM2.5 inhaled during this route
  // Running breathing rate ~25 L/min, converted to m³: /1000
  const pm25 = aqiToPm25(aqi);
  const durationMin = durationSec / 60;
  const breathingExposureMcg = Math.round(pm25 * 25 * durationMin / 1000 * 10) / 10;
  const { label: exposureLabel, sublabel: exposureSublabel, color: exposureColor, bg: exposureBg } = getExposureInfo(breathingExposureMcg);

  return {
    aqi, aqiLabel, aqiColor,
    elevationGain, elevationLoss,
    pacePerKm, quietRoadPct, roadBreakdown, direction,
    breathingExposureMcg, exposureLabel, exposureSublabel, exposureColor, exposureBg,
    shadeScore,
    humidity: routeWeather?.humidity ?? 70,
    temperature: routeWeather?.temperature ?? 28,
  };
}

// ── Types ───────────────────────────────────────────────────

interface Message {
  id: string;
  type: 'ai' | 'user';
  content: string;
  timestamp: Date;
}

interface LocationData {
  name: string;
  coords?: { lat: number; lng: number };
}

interface RoutePrefs {
  purpose: string;
  purposeRaw: string; // The user's actual words (e.g. "jog", "run", "walk to the park")
  startLocation: LocationData | null;
  destination: LocationData | null;
  loopDistance: number;
  preferences: string[];
  concerns: string[];
}

interface RouteMetrics {
  aqi: number;
  aqiLabel: string;
  aqiColor: string;
  elevationGain: number;
  elevationLoss: number;
  pacePerKm: string;
  quietRoadPct: number; // % on residential/path/park
  roadBreakdown: string; // e.g. "Mostly residential"
  direction: string; // e.g. "North", "Southeast"
  breathingExposureMcg: number; // estimated PM2.5 inhaled in µg
  exposureLabel: string;
  exposureSublabel: string;
  exposureColor: string;
  exposureBg: string;
  shadeScore: number;    // 0–100, from OSM tree/park density
  humidity: number;      // % relative humidity at route midpoint
  temperature: number;   // °C at route midpoint
}

interface RouteOption {
  id: string;
  name: string;
  distance: string;
  duration: string;
  distanceKm: number;
  durationSec: number;
  metrics?: RouteMetrics;
  _coords?: [number, number][];
  badge?: string; // e.g. "Flattest", "Cleanest Air", "Quietest"
}

type Phase =
  | 'greeting'
  | 'gathering'
  | 'confirming'
  | 'generating'
  | 'selecting'
  | 'ready';

// ── Intent Parser ───────────────────────────────────────────

const PURPOSE_MAP: [RegExp, string][] = [
  [/\b(run(ning)?|jog(ging)?|exercise|fitness|workout|training|sprint)\b/i, 'Exercise / Running'],
  [/\b(commut|office|school|getting somewhere)\b/i, 'Commute / Getting somewhere'],
  [/\b(leisure|relax|casual|stroll|wander|unwind|chill)\b/i, 'Leisure walk'],
  [/\b(explor|sightsee|tourist|visit|discover|adventure|look around)\b/i, 'Exploring / Sightseeing'],
];

const PREF_MAP: [RegExp, string][] = [
  [/\b(shade|shad[ey]|cool|sun protect|cover(ed)?)\b/i, 'shaded paths'],
  [/\b(air quality|clean air|fresh air|pollution|aqi)\b/i, 'good air quality'],
  [/\b(quiet|peaceful|calm|tranquil|serene)\b/i, 'quiet areas'],
  [/\b(scenic|view|beautiful|pretty|picturesque)\b/i, 'scenic views'],
  [/\b(caf[eé]|coffee|restaurant|food|shop)\b/i, 'cafes & restaurants'],
  [/\b(park|garden|tree|forest|green(ery)?|nature)\b/i, 'parks & greenery'],
  [/\b(safe|well[- ]lit|secure|bright)\b/i, 'well-lit paths'],
  [/\b(flat|easy|gentle|no hill|level)\b/i, 'flat terrain'],
  [/\b(hill(y|s)?|elevat(ed|ion)|steep|climb|uphill|challenging terrain|incline)\b/i, 'hilly terrain'],
  [/\b(fast|quick|short(est)?|direct|efficient)\b/i, 'shortest path'],
  [/\b(water(front)?|river|lake|canal|beach|coast)\b/i, 'waterfront'],
];

const CONCERN_MAP: [RegExp, string][] = [
  [/\b(asthma|breathing|respiratory|lung)\b/i, 'respiratory sensitivity'],
  [/\b(allerg|pollen|hay fever)\b/i, 'allergies'],
  [/\b(avoid|no).{0,15}(traffic|busy|road|highway|car)\b/i, 'avoid busy roads'],
  [/\b(wheelchair|accessibility|accessible|mobility)\b/i, 'accessibility needs'],
  [/\b(dog|pet)\b/i, 'pet-friendly'],
  [/\b(child|kid|stroller|baby|family)\b/i, 'family-friendly'],
];

function parseIntent(text: string) {
  const lower = text.toLowerCase().trim();

  const result = {
    purpose: null as string | null,
    fromLocation: null as string | null,
    toLocation: null as string | null,
    preferences: [] as string[],
    concerns: [] as string[],
    distance: null as number | null,
    wantsGPS: false,
    wantsLoop: false,
    isConfirm: false,
    isReject: false,
    isQuestion: false,
    raw: text,
  };

  // Detect questions — "are you sure?", "how long?", "is it safe?", "can you do under 10 min?"
  const isQuestion = /\?$/.test(text.trim()) ||
    /^(are|is|can|will|how|what|where|do|does|could|would|should|why|which|when|have|has)\b/i.test(lower) ||
    /\b(right\??|correct\??)$/i.test(lower);

  if (isQuestion) {
    result.isQuestion = true;
  }

  // Always parse EVERYTHING first — never early-return.
  // This way "I want more shade, yes" extracts BOTH shade AND confirmation.

  // Purpose
  for (const [pat, purpose] of PURPOSE_MAP) {
    if (pat.test(lower)) { result.purpose = purpose; break; }
  }

  // Preferences
  for (const [pat, pref] of PREF_MAP) {
    if (pat.test(lower)) result.preferences.push(pref);
  }

  // Concerns
  for (const [pat, concern] of CONCERN_MAP) {
    if (pat.test(lower)) result.concerns.push(concern);
  }

  // GPS
  if (/\b(current location|my location|where i am|gps|my place)\b/i.test(lower)) {
    result.wantsGPS = true;
  }

  // Loop
  if (/\b(loop|circle|round trip|back to start|return to start)\b/i.test(lower)) {
    result.wantsLoop = true;
  }

  // Distance
  const dist = lower.match(/(\d+(?:\.\d+)?)\s*(km|kilometer|mile|mi|k)\b/i);
  if (dist) {
    result.distance = parseFloat(dist[1]);
    if (/mi/i.test(dist[2])) result.distance *= 1.6;
  }

  // Skip location extraction for vague/conversational phrases
  const isVagueLocation = /\b(near(?:est|by)?\s+(me|here|my)|close(?:st)?\s+to\s+me|around\s+here|somewhere|anywhere|a\s+\w+\s+near)\b/i.test(lower);

  if (!isVagueLocation) {
    // "from X to Y"
    const fromTo = text.match(/from\s+(.+?)\s+to\s+(.+?)(?:\s+(?:with|avoiding|prefer|want|looking|,)|$)/i);
    if (fromTo) {
      result.fromLocation = fromTo[1].trim();
      result.toLocation = fromTo[2].trim();
    } else {
      // "to X" alone
      const toM = text.match(/(?:to|towards?|heading to)\s+(.+?)(?:\s+(?:with|avoiding|prefer|,)|$)/i);
      if (toM && !result.purpose?.includes('Commute')) {
        result.toLocation = toM[1].trim();
      }
      // "from X" alone
      const fromM = text.match(/(?:from|starting (?:at|from)|leaving)\s+(.+?)(?:\s+(?:to|with|avoiding|,)|$)/i);
      if (fromM) {
        result.fromLocation = fromM[1].trim();
      }
    }
  }

  // Confirmation — but NOT if the user is asking a question
  // "are you sure you can do it?" is NOT a confirmation. "sure, let's go" IS.
  if (!isQuestion) {
    if (/\b(yes|yeah|yep|sure|ok(ay)?|confirm|looks? good|perfect|go ahead|let'?s? go|generate|do it|sounds? good|absolutely|yup)\b/i.test(lower)) {
      result.isConfirm = true;
    }
  }

  // Rejection / change — only if there's no new useful info being added
  const hasNewInfo = result.purpose !== null || result.preferences.length > 0 || result.concerns.length > 0 ||
    result.wantsGPS || result.wantsLoop || result.fromLocation !== null || result.toLocation !== null || result.distance !== null;

  if (!hasNewInfo && !isQuestion && /\b(no\b|nah|nope|not really|change|wait|hold on|not quite|let me change)\b/i.test(lower)) {
    result.isReject = true;
  }

  return result;
}

function getMissing(p: RoutePrefs) {
  const m: string[] = [];
  if (!p.purpose) m.push('purpose');
  if (!p.startLocation) m.push('start');
  if (!p.destination && p.loopDistance === 0) m.push('destination');
  return m;
}

function formatSummary(p: RoutePrefs) {
  const l: string[] = [];
  // Use the user's own words for purpose, not internal category
  if (p.purpose) {
    const display = p.purposeRaw || friendlyPurpose(p.purpose);
    l.push(`  Purpose: ${display.charAt(0).toUpperCase() + display.slice(1)}`);
  }
  if (p.startLocation) l.push(`  From: ${p.startLocation.name}`);
  if (p.loopDistance > 0) l.push(`  Route: ${p.loopDistance}km loop`);
  else if (p.destination) l.push(`  To: ${p.destination.name}`);
  if (p.preferences.length) l.push(`  Priorities: ${p.preferences.join(', ')}`);
  if (p.concerns.length) l.push(`  Considerations: ${p.concerns.join(', ')}`);
  return l.join('\n');
}

// ── Component ───────────────────────────────────────────────

const purposeOptions = [
  { label: 'Go for a run', internalPurpose: 'Exercise / Running', image: '/purpose-running.jpg' },
  { label: 'Get somewhere', internalPurpose: 'Commute / Getting somewhere', image: '/purpose-commute.jpg' },
  { label: 'Take a walk', internalPurpose: 'Leisure walk', image: '/purpose-leisure.jpg' },
  { label: 'Explore around', internalPurpose: 'Exploring / Sightseeing', image: '/purpose-explore.jpg' },
];

// Map internal purpose categories to friendly display names
function friendlyPurpose(internal: string, userRaw?: string): string {
  if (userRaw) return userRaw; // Always prefer the user's own words
  const map: Record<string, string> = {
    'Exercise / Running': 'a run',
    'Commute / Getting somewhere': 'getting somewhere',
    'Leisure walk': 'a walk',
    'Exploring / Sightseeing': 'exploring',
  };
  return map[internal] || internal.toLowerCase();
}

/** Embedded forecast card for chat — shows 24h AQI forecast inline */
function ForecastCard() {
  const [times, setTimes] = useState<Array<{
    time_slot: string; predicted_aqi: number; temperature: number;
    humidity: number; uv_index: number; recommendation: string; reason: string;
  }>>([]);

  useEffect(() => {
    // Generate forecast data (same logic as useRunningStore.fetchBestRunningTimes)
    const currentHour = new Date().getHours();
    const data = [];
    for (let i = 0; i < 24; i++) {
      const hour = (currentHour + i) % 24;
      const slot = `${hour.toString().padStart(2, '0')}:00`;
      const isMorning = hour >= 5 && hour <= 8;
      const isEvening = hour >= 17 && hour <= 20;
      const isMidday = hour >= 11 && hour <= 15;
      let aqi = 50, temp = 25, rec = 'good' as string, reason = '';
      if (isMorning) { aqi = 35; temp = 22; rec = 'excellent'; reason = 'Low pollution, cool temperature'; }
      else if (isEvening) { aqi = 45; temp = 26; rec = 'good'; reason = 'Moderate conditions'; }
      else if (isMidday) { aqi = 75; temp = 32; rec = 'avoid'; reason = 'High UV, peak heat'; }
      else { aqi = 55; temp = 24; rec = 'moderate'; reason = 'Acceptable conditions'; }
      data.push({ time_slot: slot, predicted_aqi: aqi, temperature: temp, humidity: 65, uv_index: isMidday ? 8 : isMorning ? 3 : 2, recommendation: rec, reason });
    }
    setTimes(data);
  }, []);

  const best = times.length > 0 ? times.reduce((a, b) => (a.recommendation === 'excellent' && b.recommendation !== 'excellent') ? a : (b.recommendation === 'excellent' && a.recommendation !== 'excellent') ? b : a.predicted_aqi < b.predicted_aqi ? a : b) : null;
  const formatH = (s: string) => { const h = parseInt(s); if (h === 0) return '12AM'; if (h < 12) return `${h}AM`; if (h === 12) return '12PM'; return `${h-12}PM`; };
  const dotColor = (r: string) => r === 'excellent' ? 'bg-[#6b9f78]' : r === 'good' ? 'bg-[#8a9ba8]' : r === 'moderate' ? 'bg-[#b89f6a]' : 'bg-[#a87272]';
  const barColor = (r: string) => r === 'excellent' ? 'bg-[#6b9f78]/60' : r === 'good' ? 'bg-[#8a9ba8]/50' : r === 'moderate' ? 'bg-[#b89f6a]/50' : 'bg-[#a87272]/50';
  const label = (r: string) => r === 'excellent' ? 'Optimal' : r === 'good' ? 'Fair' : r === 'moderate' ? 'Moderate' : 'Poor';

  if (times.length === 0) return null;

  return (
    <div className="w-full max-w-[90%] rounded-2xl bg-[#161920] border border-slate-800 overflow-hidden">
      {/* Header */}
      {best && (
        <div className="p-4 border-b border-slate-800">
          <p className="text-[10px] text-slate-500 uppercase tracking-widest mb-1">Best time to run</p>
          <div className="flex items-baseline justify-between">
            <span className="text-2xl font-light text-white">{formatH(best.time_slot)}</span>
            <span className="text-xs text-[#6b9f78]">{label(best.recommendation)}</span>
          </div>
          <div className="flex gap-3 text-xs text-slate-500 mt-1">
            <span>AQI {best.predicted_aqi}</span>
            <span>{best.temperature}°C</span>
            <span>UV {best.uv_index}</span>
          </div>
        </div>
      )}
      {/* Chart */}
      <div className="p-3">
        <div className="flex gap-[2px] items-end h-14 mb-1">
          {times.slice(0, 24).map((t, i) => {
            const maxAqi = Math.max(...times.map(s => s.predicted_aqi), 1);
            return (
              <div key={i} className={`flex-1 rounded-[1px] ${barColor(t.recommendation)}`}
                style={{ height: `${Math.max(6, (t.predicted_aqi / maxAqi) * 100)}%` }} />
            );
          })}
        </div>
        <div className="flex justify-between text-[9px] text-slate-600 font-mono">
          <span>Now</span><span>+6h</span><span>+12h</span><span>+18h</span><span>+24h</span>
        </div>
      </div>
      {/* Hourly list (top 8 slots) */}
      <div className="border-t border-slate-800 divide-y divide-slate-800/60">
        {times.slice(0, 8).map((t, i) => (
          <div key={i} className="flex items-center justify-between px-3 py-2">
            <div className="flex items-center gap-2">
              <span className={`w-1.5 h-1.5 rounded-full ${dotColor(t.recommendation)}`} />
              <span className="text-xs text-slate-300 tabular-nums w-12">{formatH(t.time_slot)}</span>
              <span className="text-[11px] text-slate-600">AQI {t.predicted_aqi}</span>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-[11px] text-slate-600">{t.temperature}°</span>
              <span className={`text-[10px] ${t.recommendation === 'excellent' ? 'text-[#6b9f78]' : t.recommendation === 'avoid' ? 'text-[#a87272]' : 'text-slate-500'}`}>
                {label(t.recommendation)}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Inline Route Air Quality Map ─────────────────────────────

const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_TOKEN || 'pk.eyJ1Ijoid2VubWlueWFwIiwiYSI6ImNta3g3OThuazA2ejAzZHF1M3d0eWZudnEifQ.LXK4nArE7sTpjCtTlwdFMw';

interface RouteMapData {
  id: string;
  name: string;
  distance: string;
  duration: string;
  coords: [number, number][];
  metrics?: RouteMetrics;
  steps?: any[];
}

interface AqiSegment {
  coords: [number, number][];
  aqi: number;
  adjustedAqi: number;
  color: string;
  distanceKm: number;
  roadType: string;       // 'park' | 'main' | 'residential'
  roadLabel: string;      // display label
  elevationDelta: number; // +m uphill / -m downhill
}

function RouteMapCard({ routes }: { routes: RouteMapData[] }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<any>(null);
  const [active, setActive] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [allSegments, setAllSegments] = useState<AqiSegment[][]>([]);
  const allSegmentsRef = useRef<AqiSegment[][]>([]);
  const [humidity, setHumidity] = useState<number | null>(null);

  // Calculate geodesic distance between two coords (km)
  const haversine = (a: [number, number], b: [number, number]) => {
    const R = 6371;
    const dLat = (b[1] - a[1]) * Math.PI / 180;
    const dLng = (b[0] - a[0]) * Math.PI / 180;
    const s = Math.sin(dLat / 2) ** 2 + Math.cos(a[1] * Math.PI / 180) * Math.cos(b[1] * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(s), Math.sqrt(1 - s));
  };

  const segmentDistKm = (seg: AqiSegment) => {
    let d = 0;
    for (let i = 1; i < seg.coords.length; i++) d += haversine(seg.coords[i - 1], seg.coords[i]);
    return d;
  };

  const drawRoute = (map: any, mapboxgl: any, idx: number, segments: AqiSegment[][]) => {
    // Remove previous layers
    ['aq-route-bg', 'aq-route-segs'].forEach(id => {
      if (map.getLayer(id)) map.removeLayer(id);
    });
    ['aq-route-bg-src', 'aq-route-segs-src'].forEach(id => {
      if (map.getSource(id)) map.removeSource(id);
    });

    const route = routes[idx];
    if (!route || !segments[idx]) return;

    // Background (grey) full route line
    map.addSource('aq-route-bg-src', {
      type: 'geojson',
      data: { type: 'Feature', properties: {}, geometry: { type: 'LineString', coordinates: route.coords } },
    });
    map.addLayer({ id: 'aq-route-bg', type: 'line', source: 'aq-route-bg-src', paint: { 'line-color': '#334155', 'line-width': 6, 'line-cap': 'round' } });

    // Colored segments
    const features = segments[idx].map(seg => ({
      type: 'Feature' as const,
      properties: { color: seg.color },
      geometry: { type: 'LineString' as const, coordinates: seg.coords },
    }));
    map.addSource('aq-route-segs-src', { type: 'geojson', data: { type: 'FeatureCollection', features } });
    map.addLayer({
      id: 'aq-route-segs', type: 'line', source: 'aq-route-segs-src',
      paint: { 'line-color': ['get', 'color'], 'line-width': 5, 'line-cap': 'round', 'line-join': 'round' },
    });

    // Start/end markers
    if (route.coords.length > 0) {
      new mapboxgl.Marker({ color: '#6b9f78', scale: 0.7 }).setLngLat(route.coords[0]).addTo(map);
      new mapboxgl.Marker({ color: '#a87272', scale: 0.7 }).setLngLat(route.coords[route.coords.length - 1]).addTo(map);
    }

    // Fit bounds
    const bounds = new mapboxgl.LngLatBounds();
    route.coords.forEach(c => bounds.extend(c));
    map.fitBounds(bounds, { padding: { top: 40, bottom: 40, left: 24, right: 24 }, duration: 600 });
  };

  // Init map once
  useEffect(() => {
    if (!containerRef.current || routes.length === 0) return;

    let cancelled = false;

    import('mapbox-gl').then(async ({ default: mapboxgl }) => {
      if (cancelled || !containerRef.current) return;
      mapboxgl.accessToken = MAPBOX_TOKEN;

      const map = new mapboxgl.Map({
        container: containerRef.current!,
        style: 'mapbox://styles/mapbox/dark-v11',
        interactive: true,
        pitch: 0,
        bearing: 0,
        attributionControl: false,
        logoPosition: 'bottom-right',
      });
      mapRef.current = map;

      // Classify road type from Mapbox step data near a coordinate
      const classifyRoad = (coord: [number, number], steps: any[]): { roadType: string; roadLabel: string; aqiMod: number } => {
        if (!steps?.length) return { roadType: 'residential', roadLabel: 'Residential', aqiMod: 1.0 };
        let nearest = steps[0];
        let minD = Infinity;
        for (const s of steps) {
          const [lng, lat] = s.maneuver?.location || [0, 0];
          const d = Math.abs(lat - coord[1]) + Math.abs(lng - coord[0]);
          if (d < minD) { minD = d; nearest = s; }
        }
        const name = ((nearest?.name || '') + ' ' + (nearest?.instruction || '')).toLowerCase();
        if (/park|taman|garden|trail|lake|waterfront|promenade|jogging|hiking|forest|rimba/i.test(name))
          return { roadType: 'park', roadLabel: 'Park / Trail', aqiMod: 0.78 };
        if (/highway|expressway|lebuhraya|federal|besar|boulevard|avenue|main|trunk/i.test(name))
          return { roadType: 'main', roadLabel: 'Main road', aqiMod: 1.32 };
        return { roadType: 'residential', roadLabel: 'Residential', aqiMod: 1.0 };
      };

      // Fetch elevation for all sample points in one batched request
      const fetchElevBatch = async (pts: [number, number][]): Promise<number[]> => {
        if (!pts.length) return [];
        const lats = pts.map(p => p[1].toFixed(4)).join(',');
        const lngs = pts.map(p => p[0].toFixed(4)).join(',');
        try {
          const r = await fetch(`https://api.open-meteo.com/v1/elevation?latitude=${lats}&longitude=${lngs}`);
          const d = await r.json();
          return d.elevation || pts.map(() => 0);
        } catch { return pts.map(() => 0); }
      };

      // Build segments for one route
      const fetchSegments = async (route: RouteMapData): Promise<AqiSegment[]> => {
        const coords = route.coords;
        const steps = route.steps || [];
        const baseAqi = route.metrics?.aqi ?? 60;
        if (coords.length === 0) return [];

        const N = 4;
        const step = Math.max(1, Math.floor(coords.length / N));
        const indices: number[] = [0];
        for (let i = step; i < coords.length && indices.length < N; i += step) indices.push(i);
        if (indices[indices.length - 1] !== coords.length - 1) indices.push(coords.length - 1);

        // Batch elevation fetch for all sample points
        const samplePts = indices.map(i => coords[i]);
        const elevations = await fetchElevBatch(samplePts);

        return indices.slice(0, -1).map((startIdx, i) => {
          const endIdx = indices[i + 1];
          const segCoords = coords.slice(startIdx, endIdx + 1);
          const midPt = segCoords[Math.floor(segCoords.length / 2)];
          const { roadType, roadLabel, aqiMod } = classifyRoad(midPt, steps);
          const adjustedAqi = Math.round(baseAqi * aqiMod);
          const { color } = getAqiInfo(adjustedAqi);
          const elevDelta = elevations.length > i + 1 ? Math.round(elevations[i + 1] - elevations[i]) : 0;
          return { coords: segCoords, aqi: baseAqi, adjustedAqi, color, distanceKm: 0, roadType, roadLabel, elevationDelta: elevDelta };
        });
      };

      // Run map load AND AQI fetch in parallel — don't block map on AQI
      const mapLoadPromise = new Promise<void>(resolve => {
        if (map.loaded()) resolve();
        else map.once('load', () => resolve());
      });

      const segmentsPromise = Promise.all(routes.map(r => fetchSegments(r)));

      const [, segments] = await Promise.all([mapLoadPromise, segmentsPromise]);
      if (cancelled) return;

      // Calculate distances
      const withDist = segments.map(segs => segs.map(seg => ({ ...seg, distanceKm: segmentDistKm(seg) })));
      setAllSegments(withDist);
      allSegmentsRef.current = withDist;

      drawRoute(map, mapboxgl, 0, withDist);
      setIsLoading(false);

      // Fetch humidity for the route midpoint (non-blocking)
      const mid = routes[0]?.coords[Math.floor((routes[0]?.coords.length || 0) / 2)];
      if (mid) {
        fetch(`https://api.open-meteo.com/v1/forecast?latitude=${mid[1].toFixed(4)}&longitude=${mid[0].toFixed(4)}&current=relative_humidity_2m&forecast_days=1`)
          .then(r => r.json())
          .then(d => { if (!cancelled) setHumidity(d?.current?.relative_humidity_2m ?? null); })
          .catch(() => {});
      }
    });

    return () => {
      cancelled = true;
      mapRef.current?.remove();
      mapRef.current = null;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routes]);

  // Switch route
  useEffect(() => {
    if (!mapRef.current || allSegmentsRef.current.length === 0 || isLoading) return;
    import('mapbox-gl').then(({ default: mapboxgl }) => {
      drawRoute(mapRef.current, mapboxgl, active, allSegmentsRef.current);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  const activeSegs = allSegments[active] || [];
  const avgAqi = activeSegs.length > 0 ? Math.round(activeSegs.reduce((s, seg) => s + seg.aqi, 0) / activeSegs.length) : 0;
  const activeRoute = routes[active];
  const pm25 = aqiToPm25(avgAqi);
  const durationMin = activeRoute ? parseInt(activeRoute.duration) : 30;
  const totalExposure = Math.round(pm25 * 25 * durationMin / 1000 * 10) / 10;
  const { label: expLabel, color: expColor } = getExposureInfo(totalExposure);

  // Running total distance for segment labels
  let runningDist = 0;

  return (
    <div className="w-full max-w-[92%] rounded-2xl bg-[#0f1117] border border-slate-800 overflow-hidden">
      {/* Route tabs */}
      {routes.length > 1 && (
        <div className="flex border-b border-slate-800">
          {routes.map((r, i) => (
            <button
              key={r.id}
              onClick={() => setActive(i)}
              className={`flex-1 py-2.5 text-[11px] font-medium tracking-wide transition-colors ${
                i === active
                  ? 'text-white border-b-2 border-[#00ffd0] bg-white/[0.03]'
                  : 'text-slate-500 hover:text-slate-300'
              }`}
            >
              {r.name}
            </button>
          ))}
        </div>
      )}

      {/* Map */}
      <div className="relative" style={{ height: '220px' }}>
        <div ref={containerRef} className="w-full h-full [&_.mapboxgl-ctrl-logo]:hidden [&_.mapboxgl-ctrl-bottom-right]:hidden [&_.mapboxgl-ctrl-attrib]:hidden" />
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-[#0f1117]/80">
            <div className="flex flex-col items-center gap-2">
              <div className="w-5 h-5 border-2 border-slate-700 border-t-[#00ffd0] rounded-full animate-spin" />
              <span className="text-[10px] text-slate-500 uppercase tracking-widest">Analysing air quality</span>
            </div>
          </div>
        )}
        {/* Map label */}
        {!isLoading && (
          <div className="absolute top-2 left-2 bg-black/60 backdrop-blur-sm rounded-lg px-2 py-1">
            <span className="text-[9px] text-slate-400 uppercase tracking-widest">Air quality along route</span>
          </div>
        )}
      </div>

      {/* AQI legend */}
      {!isLoading && (
        <div className="flex items-center gap-3 px-3 py-2 border-t border-slate-800 bg-white/[0.02]">
          {[{ color: '#22c55e', label: 'Good' }, { color: '#eab308', label: 'Moderate' }, { color: '#f97316', label: 'Sensitive' }, { color: '#ef4444', label: 'Unhealthy' }].map(l => (
            <div key={l.label} className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: l.color }} />
              <span className="text-[9px] text-slate-500">{l.label}</span>
            </div>
          ))}
        </div>
      )}

      {/* Segment breakdown */}
      {!isLoading && activeSegs.length > 0 && (
        <div className="border-t border-slate-800 divide-y divide-slate-800/60">
          {activeSegs.map((seg, i) => {
            const from = runningDist;
            runningDist += seg.distanceKm;
            const to = runningDist;
            const { label: airLabel } = getAqiInfo(seg.adjustedAqi);
            const elevSign = seg.elevationDelta > 2 ? '+' : '';
            const elevText = Math.abs(seg.elevationDelta) < 2 ? 'Flat' : `${elevSign}${seg.elevationDelta}m`;
            const roadColor = seg.roadType === 'park' ? '#6b9f78' : seg.roadType === 'main' ? '#a87272' : '#8a9ba8';
            // PM2.5 inhaled for this segment specifically
            const segPm25 = aqiToPm25(seg.adjustedAqi);
            const paceMin = activeRoute?.metrics?.pacePerKm
              ? parseInt(activeRoute.metrics.pacePerKm) + parseInt((activeRoute.metrics.pacePerKm.split(':')[1] || '0')) / 60
              : 7;
            const segDurMin = seg.distanceKm * paceMin;
            const segExposure = Math.round(segPm25 * 25 * segDurMin / 1000 * 10) / 10;
            return (
              <div key={i} className="px-3 py-2.5">
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: seg.color }} />
                    <span className="text-[11px] text-slate-400 tabular-nums">{from.toFixed(1)}–{to.toFixed(1)} km</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[11px] font-medium" style={{ color: seg.color }}>{airLabel}</span>
                    <span className="text-[12px] font-semibold tabular-nums" style={{ color: seg.color }}>~{segExposure}µg</span>
                  </div>
                </div>
                <div className="flex items-center gap-3 pl-4">
                  <span className="text-[10px] font-medium px-1.5 py-0.5 rounded" style={{ color: roadColor, background: `${roadColor}18` }}>{seg.roadLabel}</span>
                  <span className="text-[10px] text-slate-600">{elevText}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Summary footer */}
      {!isLoading && (
        <div className="flex items-center justify-between px-3 py-3 border-t border-slate-800 bg-white/[0.02]">
          <div className="flex items-center gap-2">
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ color: expColor }}>
              <path d="M12 4c0 0 0 3-3 5s-6 2-6 7c0 2.5 2 4 4 4s4-2 4-4V4z" />
              <path d="M12 4c0 0 0 3 3 5s6 2 6 7c0 2.5-2 4-4 4s-4-2-4-4V4z" />
            </svg>
            <span className="text-[12px] font-medium" style={{ color: expColor }}>{expLabel}</span>
            <span className="text-[10px] text-slate-600">avg AQI {avgAqi}</span>
            {humidity !== null && (
              <span className="text-[10px] text-slate-500">· {humidity}% humidity</span>
            )}
          </div>
          <div className="text-right">
            <span className="text-[13px] font-semibold tabular-nums" style={{ color: expColor }}>~{totalExposure} µg</span>
            <span className="text-[10px] text-slate-600 ml-1">PM2.5 inhaled</span>
          </div>
        </div>
      )}
    </div>
  );
}

export function RouteConfirmation() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialQuery = searchParams.get('q') || '';
  const { hasAccess: hasRoutePlanning } = useFeatureAccess('routePlanning');

  const [messages, setMessages] = useState<Message[]>([]);
  const [userInput, setUserInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [phase, setPhase] = useState<Phase>('greeting');
  const [prefs, setPrefs] = useState<RoutePrefs>({
    purpose: '',
    purposeRaw: '',
    startLocation: null,
    destination: null,
    loopDistance: 0,
    preferences: [],
    concerns: [],
  });
  const [routeOptions, setRouteOptions] = useState<RouteOption[]>([]);
  const [recommendedRouteId, setRecommendedRouteId] = useState<string | null>(null);
  const [routeMapData, setRouteMapData] = useState<RouteMapData[]>([]);
  const [, setSelectedRoute] = useState<RouteOption | null>(null);
  const [isLoadingGPS, setIsLoadingGPS] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const [thinkingSteps, setThinkingSteps] = useState<string[]>([]);
  const [pendingRouteData, setPendingRouteData] = useState<any>(null);
  const showRouteMapAfterGenerateRef = useRef(false);
  const [cachedMapboxData, setCachedMapboxData] = useState<any>(null);
  const [cachedAllRoutes, setCachedAllRoutes] = useState<any[]>([]);
  // Mirror in ref to prevent stale closures in callbacks
  const cachedAllRoutesRef = useRef<any[]>([]);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // ── Helpers ──

  const scrollToBottom = useCallback(() => {
    setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
  }, []);

  useEffect(() => { scrollToBottom(); }, [messages, scrollToBottom]);

  const addMsg = useCallback((type: 'ai' | 'user', content: string) => {
    setMessages(prev => [...prev, {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      type,
      content,
      timestamp: new Date(),
    }]);
  }, []);

  const aiSay = useCallback((content: string, delay = 1200) => {
    return new Promise<void>(resolve => {
      setIsTyping(true);
      setTimeout(() => {
        setIsTyping(false);
        addMsg('ai', content);
        resolve();
      }, delay);
    });
  }, [addMsg]);

  const geocode = useCallback(async (query: string, nearbyCoords?: { lat: number; lng: number }): Promise<LocationData | null> => {
    try {
      // Extract POI keyword from various "near me" patterns
      // "Which is the nearest park near me?" → "park"
      // "Find a cafe near me" → "cafe"
      // "Nearest park" → "park"
      // "Choose a park nearest me" → "park"
      const nearMeMatch =
        query.match(/(?:which|where|what)(?:'s|\s+is)\s+(?:the\s+)?(?:nearest|closest)\s+(\w[\w\s]*?)(?:\s+(?:near|close\s+to|around|next\s+to|by)\s+(?:me|here|us|my\s+location))?[?.!]?\s*$/i)
        || query.match(/(?:choose|find|pick|get|go to|head to)\s+(?:a|the|an)\s+(.+?)\s+(?:near(?:est)?|close(?:st)?|around|by|next to)\s+(?:me|here|my location|us)/i)
        || query.match(/^(?:the\s+)?(?:nearest|closest|nearby)\s+(.+?)(?:\s+(?:near|close\s+to|to|from)\s+(?:me|here|us))?[?.!]?\s*$/i);

      const searchQuery = nearMeMatch ? nearMeMatch[1].trim() : query;

      const results = await searchPlaces(searchQuery, {
        limit: 1,
        userLocation: nearbyCoords || undefined,
      });
      if (results.length > 0) {
        return {
          name: results[0].name || searchQuery,
          coords: { lat: results[0].coordinates.lat, lng: results[0].coordinates.lng },
        };
      }
      return null;
    } catch {
      return null;
    }
  }, []);

  const getGPS = useCallback((): Promise<{ lat: number; lng: number } | null | 'denied'> => {
    return new Promise(resolve => {
      if (!navigator.geolocation) { resolve(null); return; }
      navigator.geolocation.getCurrentPosition(
        pos => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
        err => resolve(err.code === 1 ? 'denied' : null),
        { enableHighAccuracy: false, timeout: 15000, maximumAge: 60000 }
      );
    });
  }, []);

  // ── AI chat helper ──

  const askAI = useCallback(async (userMessage: string, currentPrefs: RoutePrefs, currentPhase: Phase): Promise<{
    message: string;
    extracted: {
      purpose?: string | null;
      purposeRaw?: string | null;
      preferences?: string[];
      concerns?: string[];
      isConfirm?: boolean;
      isReject?: boolean;
      wantsLoop?: boolean;
      distance?: number | null;
      fromLocation?: string | null;
      toLocation?: string | null;
    };
  } | null> => {
    try {
      const missing = getMissing(currentPrefs);
      const response = await fetch('/api/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userMessage,
          routeState: {
            purpose: currentPrefs.purpose,
            purposeRaw: currentPrefs.purposeRaw,
            startLocation: currentPrefs.startLocation?.name || null,
            destination: currentPrefs.destination?.name || null,
            loopDistance: currentPrefs.loopDistance,
            preferences: currentPrefs.preferences,
            concerns: currentPrefs.concerns,
            phase: currentPhase,
            missing,
            hasGPS: true,
            startAlreadySet: !!currentPrefs.startLocation,
            destinationAlreadySet: !!currentPrefs.destination || currentPrefs.loopDistance > 0,
            userNote: 'GPS is enabled. "near me" / "my location" / "current location" → set fromLocation to "GPS", never ask for a place name. If startLocation is already set, NEVER ask for start again. If destination is already set, NEVER ask for destination again.',
            routeOptions: routeOptions.map(r => ({ name: r.name, distance: r.distance, duration: r.duration })),
          },
          conversationHistory: messages.slice(-6).map(m => ({ type: m.type, content: m.content })),
        }),
      });

      if (!response.ok) return null;
      return await response.json();
    } catch {
      return null;
    }
  }, [messages, routeOptions]);

  // ── Accept pending route (user confirmed distance mismatch) ──

  const acceptPendingRoute = useCallback(async () => {
    if (!pendingRouteData) return;
    const routeData = pendingRouteData;
    setPendingRouteData(null);

    const distanceKm = (routeData.totalDistance / 1000).toFixed(1);
    const durationMin = Math.round(routeData.totalDuration / 60);
    const durStr = durationMin >= 60
      ? `${Math.floor(durationMin / 60)} hr ${durationMin % 60} min`
      : `${durationMin} min`;

    addMsg('ai', `Got it! ${distanceKm} km loop, ~${durStr}. Opening map...`);

    sessionStorage.setItem('blurp_pending_route', JSON.stringify(routeData));
    sessionStorage.setItem('blurp_preview_mode', 'true');

    await new Promise(r => setTimeout(r, 1500));
    navigate('/', { replace: true, state: { previewRoute: true } });
  }, [pendingRouteData, addMsg, navigate]);

  // ── Start navigation ──

  const handleStartNavigation = useCallback(async () => {
    // Paywall: skip if routes already generated (user proved access during generation)
    if (!hasRoutePlanning && !cachedMapboxData) {
      showUpgradeModal('routePlanning', 'pro');
      return;
    }

    if (!prefs.startLocation?.coords || (!prefs.destination?.coords && prefs.loopDistance === 0)) {
      addMsg('ai', 'Missing location data. Please provide both a start and destination.');
      return;
    }

    const start = prefs.startLocation.coords;
    const end = prefs.destination?.coords || start;

    // Use cached Mapbox data from generateRoutes if available
    let data = cachedMapboxData;

    if (!data) {
      addMsg('ai', 'Finding the best walking route for you...');
      setIsTyping(true);

      const token = import.meta.env.VITE_MAPBOX_TOKEN || 'pk.eyJ1Ijoid2VubWlueWFwIiwiYSI6ImNta3g3OThuazA2ejAzZHF1M3d0eWZudnEifQ.LXK4nArE7sTpjCtTlwdFMw';
      let waypointStr: string;

      if (prefs.loopDistance > 0) {
        const radiusKm = prefs.loopDistance / 7.5;
        const radiusDeg = radiusKm / 111;
        const lngFactor = 1 / Math.cos(start.lat * Math.PI / 180);
        const wp1 = { lng: start.lng + radiusDeg * lngFactor * 0.87, lat: start.lat + radiusDeg * 0.5 };
        const wp2 = { lng: start.lng, lat: start.lat - radiusDeg };
        const wp3 = { lng: start.lng - radiusDeg * lngFactor * 0.87, lat: start.lat + radiusDeg * 0.5 };
        waypointStr = `${start.lng},${start.lat};${wp1.lng},${wp1.lat};${wp2.lng},${wp2.lat};${wp3.lng},${wp3.lat};${start.lng},${start.lat}`;
      } else {
        waypointStr = `${start.lng},${start.lat};${end.lng},${end.lat}`;
      }

      try {
        const response = await fetch(
          `https://api.mapbox.com/directions/v5/mapbox/walking/${waypointStr}?access_token=${token}&geometries=geojson&overview=full&steps=true`
        );
        data = await response.json();
        setIsTyping(false);

        if (!response.ok || data.code !== 'Ok' || !data.routes?.length) {
          await aiSay(`Couldn't find a walking route. The locations may be too far apart — try closer destinations.`, 1200);
          return;
        }
      } catch (error) {
        setIsTyping(false);
        const msg = error instanceof Error ? error.message : String(error);
        addMsg('ai', `Route error: ${msg}. Please check your connection and try again.`);
        return;
      }
    }

    const route = data.routes[0];
    const polyline = route.geometry.coordinates.map(([lng, lat]: [number, number]) => ({ lat, lng }));
    const actualKm = route.distance / 1000;
    const durationMin = Math.round(route.duration / 60);
    const durStr = durationMin >= 60
      ? `${Math.floor(durationMin / 60)} hr ${durationMin % 60} min`
      : `${durationMin} min`;

    const steps = route.legs.flatMap((leg: any) =>
      leg.steps.map((s: any) => ({
        instruction: s.maneuver.instruction,
        distance: Math.round(s.distance),
        duration: Math.round(s.duration),
        maneuver: convertManeuver(s.maneuver.type, s.maneuver.modifier),
        coordinates: { lat: s.maneuver.location[1], lng: s.maneuver.location[0] },
      }))
    );

    // Always use ACTUAL distance in the label, never the requested distance
    const routeData = {
      id: `route_${Date.now()}`,
      priority: 'safest' as const,
      origin: { lat: start.lat, lng: start.lng },
      destination: { lat: end.lat, lng: end.lng },
      destinationName: prefs.loopDistance > 0 ? `${actualKm.toFixed(1)}km Loop` : prefs.destination?.name || 'Destination',
      totalDistance: route.distance,
      totalDuration: route.duration,
      steps,
      cognitiveScore: 85,
      arrivalNeuralIndex: 80,
      polyline,
      averageNI: 80,
      exposureScore: 20,
    };

    // Proceed to preview — distance was already validated in generateRoutes
    addMsg('ai', prefs.loopDistance > 0
      ? `Loop route ready! ${actualKm.toFixed(1)} km, ~${durStr}. Opening map...`
      : `Route found! ${actualKm.toFixed(1)} km, ~${durStr}. Opening map...`);

    sessionStorage.setItem('blurp_pending_route', JSON.stringify(routeData));
    sessionStorage.setItem('blurp_preview_mode', 'true');

    await new Promise(r => setTimeout(r, 1500));
    navigate('/', { replace: true, state: { previewRoute: true } });
  }, [prefs, cachedMapboxData, addMsg, aiSay, navigate, hasRoutePlanning]);

  // ── Main conversation handler ──

  const handleSend = useCallback(async (text: string) => {
    const trimmed = text.trim();
    if (!trimmed || isTyping) return;

    addMsg('user', trimmed);
    setUserInput('');

    // ── Check for forecast/full day request — show embedded card ──
    // ── Back to routes ──
    if (trimmed.toLowerCase() === 'back to routes') {
      addMsg('ai', 'Here are your route options:');
      return;
    }

    const wantsFullForecast = /\b(full (day|forecast|condition|schedule)|entire day|whole day|all hours|hourly forecast|show.*(forecast|schedule|breakdown)|24.hour|full condition)\b/i.test(trimmed);
    const wantsBestTime = /\b(best time|when (should|can) i run|when to run|good time (to|for)|right time|optimal time|what time|is now (a good|ok|fine)|should i run now|is it safe to run)\b/i.test(trimmed);
    if (wantsFullForecast || wantsBestTime) {
      setIsTyping(true);
      const aiResult = await askAI(trimmed, prefs, phase);
      setIsTyping(false);
      if (aiResult?.message) addMsg('ai', aiResult.message);
      addMsg('ai', '[FORECAST]');
      return;
    }

    // ── Check for "best route for my condition" request ──
    const wantsBestRoute = routeOptions.length > 0 && /\b(best.*(route|condition|body|me|my)|which.*(route|one|better|best)|recommend|suit(able)?|easiest|lowest.*(elevation|climb)|cleanest|good.*(condition|body|run)|for my (condition|body|health))\b/i.test(trimmed);
    if (wantsBestRoute) {
      // Score each route: lower exposure + lower elevation + higher quiet = better
      const scored = routeOptions
        .filter(r => r.metrics)
        .map(r => {
          const m = r.metrics!;
          const exposureScore = m.breathingExposureMcg ?? 999;
          const elevScore = m.elevationGain;
          const quietScore = 100 - m.quietRoadPct;
          // Normalise each to 0-1 range relative to the set
          return { id: r.id, name: r.name, raw: { exposureScore, elevScore, quietScore } };
        });
      if (scored.length > 0) {
        const maxExp = Math.max(...scored.map(s => s.raw.exposureScore), 1);
        const maxElev = Math.max(...scored.map(s => s.raw.elevScore), 1);
        const best = scored.reduce((a, b) => {
          const scoreA = (a.raw.exposureScore / maxExp) * 0.5 + (a.raw.elevScore / maxElev) * 0.3 + (a.raw.quietScore / 100) * 0.2;
          const scoreB = (b.raw.exposureScore / maxExp) * 0.5 + (b.raw.elevScore / maxElev) * 0.3 + (b.raw.quietScore / 100) * 0.2;
          return scoreA < scoreB ? a : b;
        });
        setRecommendedRouteId(best.id);
        setIsTyping(true);
        const aiResult = await askAI(trimmed, prefs, phase);
        setIsTyping(false);
        addMsg('ai', aiResult?.message || `${best.name} is your best option — cleanest air, least elevation, most quiet roads. It's highlighted in blue.`);
      }
      return;
    }

    // ── Check for route air quality map request ──
    const routeAirMatcher = /\b(full (report|analysis|breakdown)|how (clean|good|bad|pollut).*(air|route|run|path)|how much.*(pollut|breath|inhal)|what.*(breath|inhal|pollut)|air.*(quality|clean|pollut).*(route|run|path|along)|pollut.*(route|run|map|path)|show.*(air|pollut|map|route).*(quality|analysis|breakdown|clean)|route.*(air|clean|pollut)|clean.*(route|air)|along.*(route|run).*air|air.*along.*(route|run)|analys.*(route|air)|map.*(air|pollut))\b/i;
    const wantsRouteAirInfo = routeAirMatcher.test(trimmed);

    if (wantsRouteAirInfo) {
      if (routeMapData.length > 0) {
        // Routes exist — show the full inline air quality map
        setIsTyping(true);
        const aiResult = await askAI(trimmed, prefs, phase);
        setIsTyping(false);
        if (aiResult?.message) addMsg('ai', aiResult.message);
        else addMsg('ai', "Here's the full air quality analysis along your route — each segment is colour-coded by AQI:");
        addMsg('ai', '[ROUTE_MAP]');
      } else {
        // Routes not yet generated — flag to show route map after generation, then trigger generate
        showRouteMapAfterGenerateRef.current = true;
        setIsTyping(true);
        const aiResult = await askAI(trimmed, prefs, phase);
        setIsTyping(false);
        addMsg('ai', aiResult?.message || "Generating your routes now — I'll include the full air quality map for each one!");
        // Treat as confirmation to generate
        if (prefs.startLocation && (prefs.destination || prefs.loopDistance > 0)) {
          setPhase('generating');
          setTimeout(() => generateRoutes(prefs), 400);
        }
      }
      return;
    }

    // ── AI-FIRST APPROACH ──
    // Every message goes to AI first. AI responds conversationally AND extracts structured data.
    // We only use regex as a fallback when AI is unavailable.

    setIsTyping(true);
    const aiResult = await askAI(trimmed, prefs, phase);

    if (aiResult) {
      setIsTyping(false);

      let updated = { ...prefs };
      let hadNewInfo = false;
      const ext = aiResult.extracted || {};

      // Apply structured data the AI extracted
      if (ext.purpose && !updated.purpose) {
        updated.purpose = ext.purpose;
        updated.purposeRaw = ext.purposeRaw || '';
        hadNewInfo = true;
      }
      if (ext.preferences?.length) {
        const newP = ext.preferences.filter((p: string) => !updated.preferences.includes(p));
        if (newP.length) { updated.preferences = [...updated.preferences, ...newP]; hadNewInfo = true; }
      }
      if (ext.concerns?.length) {
        const newC = ext.concerns.filter((c: string) => !updated.concerns.includes(c));
        if (newC.length) { updated.concerns = [...updated.concerns, ...newC]; hadNewInfo = true; }
      }
      // Geocode locations extracted by AI
      if (ext.fromLocation && !updated.startLocation) {
        if (/^(gps|current location|my location|here)$/i.test(ext.fromLocation.trim())) {
          // AI recognized "near me" — try GPS. On iOS this may fail if called outside
          // a direct user gesture; in that case, nudge them to tap the GPS button.
          const coords = await getGPS();
          if (coords && coords !== 'denied') {
            updated.startLocation = { name: `Current location (${coords.lat.toFixed(4)}, ${coords.lng.toFixed(4)})`, coords };
            hadNewInfo = true;
          } else if (coords === 'denied') {
            if (aiResult.message) addMsg('ai', aiResult.message);
            await aiSay(`Location access is blocked.\n\niOS: Settings → Privacy & Security → Location Services → Safari → While Using\n\nOr type your starting address.`, 1000);
            setIsTyping(false);
            return;
          } else {
            // GPS timed out / unavailable — prompt button tap
            if (aiResult.message) addMsg('ai', aiResult.message);
            await aiSay(`Tap the "Use Current Location" button below to share your location, or type your starting address.`, 1000);
            setIsTyping(false);
            return;
          }
        } else {
          const loc = await geocode(ext.fromLocation);
          if (loc) {
            updated.startLocation = loc;
            hadNewInfo = true;
          }
        }
      }
      if (ext.toLocation && !updated.destination && updated.loopDistance === 0) {
        if (/^(gps|current location|my location|here)$/i.test(ext.toLocation.trim())) {
          const coords = await getGPS();
          if (coords && coords !== 'denied') {
            updated.destination = { name: `Current location`, coords };
            hadNewInfo = true;
          }
        } else {
          const loc = await geocode(ext.toLocation, updated.startLocation?.coords || undefined);
          if (loc) {
            updated.destination = loc;
            hadNewInfo = true;
          }
        }
      }

      if (ext.wantsLoop && updated.startLocation) {
        updated.destination = { name: 'Back to start (loop)', coords: updated.startLocation.coords };
        if (ext.distance) updated.loopDistance = ext.distance;
        hadNewInfo = true;
      }
      if (ext.distance && !ext.wantsLoop && updated.startLocation) {
        updated.loopDistance = ext.distance;
        updated.destination = { name: 'Back to start (loop)', coords: updated.startLocation.coords };
        hadNewInfo = true;
      }

      if (hadNewInfo) {
        setPrefs(updated);
      }

      // Handle confirmation — show Generate Route button or generate directly
      const aiImpliesGenerate = aiResult.message && /\b(generat|ready for you|let'?s (get|do|go|start)|creating your route|finding.+route|building.+route)\b/i.test(aiResult.message);
      if (ext.isConfirm || aiImpliesGenerate) {
        setPrefs(updated);
        const missing = getMissing(updated);
        if (missing.length === 0) {
          if (ext.isConfirm) {
            // User explicitly said "generate" / "yes" — generate immediately
            if (aiResult.message) addMsg('ai', aiResult.message);
            setPhase('generating');
            generateRoutes(updated);
          } else {
            // AI implies ready — show the Generate Route button
            setPhase('confirming');
            if (aiResult.message) addMsg('ai', aiResult.message);
          }
        } else {
          if (aiResult.message) addMsg('ai', aiResult.message);
          await aiSay(`Almost there! I still need your ${missing.join(' and ')}.`, 1000);
        }
        return;
      }

      // If all info is now complete, show the Generate Route button
      const missing = getMissing(updated);
      if (hadNewInfo && missing.length === 0 && phase !== 'confirming') {
        setPhase('confirming');
      }

      // Always show the AI's conversational response
      await aiSay(aiResult.message || "Got it! What else would you like?", 800);
      return;
    }

    // ── FALLBACK: AI unavailable — use regex parsing ──
    setIsTyping(false);

    const intent = parseIntent(trimmed);
    let updated = { ...prefs };
    const acks: string[] = [];
    let hadNewInfo = false;

    // Extract purpose
    if (intent.purpose && !updated.purpose) {
      updated.purpose = intent.purpose;
      const purposeMatch = trimmed.match(/\b(run(ning)?|jog(ging)?|exercise|fitness|workout|training|sprint|commut(e|ing)|office|school|getting somewhere|leisure|relax(ing)?|casual|stroll(ing)?|wander(ing)?|unwind|chill(ing)?|explor(e|ing)|sightsee(ing)?|tourist|visit(ing)?|discover(ing)?|adventure|look(ing)? around)\b/i);
      const userWord = purposeMatch ? purposeMatch[0] : friendlyPurpose(intent.purpose);
      updated.purposeRaw = userWord;
      acks.push(`${userWord.charAt(0).toUpperCase() + userWord.slice(1)} it is`);
      hadNewInfo = true;
    }

    // Extract preferences
    const newPrefs = intent.preferences.filter(p => !updated.preferences.includes(p));
    if (newPrefs.length > 0) {
      updated.preferences = [...updated.preferences, ...newPrefs];
      acks.push(`I'll prioritize ${newPrefs.join(' and ')}`);
      hadNewInfo = true;
    }

    // GPS request
    if (intent.wantsGPS) {
      setIsTyping(true);
      setIsLoadingGPS(true);
      const coords = await getGPS();
      setIsLoadingGPS(false);
      if (coords === 'denied') {
        await aiSay(`Location access is blocked.\n\niOS: Settings → Privacy & Security → Location Services → Safari → While Using\n\nOr type your starting address.`, 1000);
        setIsTyping(false);
        return;
      } else if (coords) {
        const locName = `Current location (${coords.lat.toFixed(4)}, ${coords.lng.toFixed(4)})`;
        if (!updated.startLocation) {
          updated.startLocation = { name: locName, coords };
          acks.push("Got your location");
        }
        hadNewInfo = true;
      }
      setIsTyping(false);
    }

    // Loop / distance
    if (intent.wantsLoop && updated.startLocation) {
      updated.destination = { name: 'Back to start (loop)', coords: updated.startLocation.coords };
      if (intent.distance) updated.loopDistance = intent.distance;
      hadNewInfo = true;
    }
    if (intent.distance && !intent.wantsLoop && updated.startLocation) {
      updated.loopDistance = intent.distance;
      updated.destination = { name: 'Back to start (loop)', coords: updated.startLocation.coords };
      hadNewInfo = true;
    }

    // Geocode specific place names (not vague phrases)
    const isVaguePhrase = /\b(near me|nearest|nearby|closest|around here|somewhere)\b/i.test(trimmed);
    if (!isVaguePhrase && (intent.fromLocation || intent.toLocation)) {
      setIsTyping(true);
      if (intent.fromLocation && !updated.startLocation) {
        const loc = await geocode(intent.fromLocation);
        if (loc) { updated.startLocation = loc; hadNewInfo = true; }
      }
      if (intent.toLocation && !updated.destination && updated.loopDistance === 0) {
        const loc = await geocode(intent.toLocation, updated.startLocation?.coords || undefined);
        if (loc) { updated.destination = loc; hadNewInfo = true; }
      }
      setIsTyping(false);
    }

    if (!hadNewInfo && !intent.isConfirm) {
      await aiSay("I'm having trouble understanding. Could you give me a specific place name, say 'loop' for a round trip, or tap the GPS button?", 1200);
      return;
    }

    // ── STEP 2: Save all extracted info ──
    setPrefs(updated);

    // ── STEP 3: Now decide what to do — based on current phase + what was extracted ──

    // If user was in selecting/ready phase but added new preferences → go back to confirming with updated plan
    if ((phase === 'selecting' || phase === 'ready') && hadNewInfo) {
      const missing = getMissing(updated);
      if (missing.length === 0) {
        setPhase('confirming');
        const summary = formatSummary(updated);
        const prefix = acks.length > 0 ? acks.join('. ') + '.\n\n' : '';
        await aiSay(`${prefix}Updated your plan:\n${summary}\n\nWant me to regenerate routes with these changes?`, 1500);
        return;
      }
    }

    // If in selecting phase and user picked a route (but no new info)
    if (phase === 'selecting' && !hadNewInfo) {
      const lower = trimmed.toLowerCase();
      const match = routeOptions.find(r =>
        lower.includes(r.name.toLowerCase()) ||
        lower.includes(r.id) ||
        (lower.includes('first') && r.id === 'a') ||
        (lower.includes('second') && r.id === 'b') ||
        (lower.includes('third') && r.id === 'c') ||
        (lower.includes('1') && r.id === 'a') ||
        (lower.includes('2') && r.id === 'b') ||
        (lower.includes('3') && r.id === 'c')
      );
      if (match) {
        handleRouteSelect(match.id);
        return;
      }
      if (intent.isReject) {
        setPhase('gathering');
        await aiSay("No problem — what would you like to change?", 1000);
        return;
      }
      await aiSay("Pick one of the routes above, or tell me what you'd like to adjust.", 1200);
      return;
    }

    // If user is confirming/rejecting a distance mismatch
    if (pendingRouteData && phase === 'ready') {
      if (intent.isConfirm || /yes|sure|ok|go|fine|accept|that'?s? (fine|ok|good)/i.test(trimmed.toLowerCase())) {
        acceptPendingRoute();
        return;
      }
      if (intent.isReject || /no|nah|change|adjust|different|shorter|longer/i.test(trimmed.toLowerCase())) {
        setPendingRouteData(null);
        setPhase('gathering');
        await aiSay("No problem! Tell me a different distance, or say 'loop' with a new distance.", 1200);
        return;
      }
      // If they typed a new distance, parse it
      const distMatch = trimmed.match(/(\d+(?:\.\d+)?)\s*(?:km|kilometer)/i);
      if (distMatch) {
        const newDist = parseFloat(distMatch[1]);
        setPendingRouteData(null);
        const updatedPrefs = { ...prefs, loopDistance: newDist };
        setPrefs(updatedPrefs);
        setPhase('confirming');
        const summary = formatSummary(updatedPrefs);
        await aiSay(`Updated to ${newDist}km loop.\n\n${summary}\n\nLooks good? Say "yes" to generate routes.`, 1500);
        return;
      }
    }

    // If in ready phase with no new info
    if (phase === 'ready' && !hadNewInfo) {
      if (intent.isConfirm || /preview|map|start|go/i.test(trimmed)) {
        handleStartNavigation();
        return;
      }
      if (intent.isReject || /different|change|back/i.test(trimmed)) {
        setPhase('selecting');
        await aiSay("Here are the route options again — pick the one that suits you best:", 1000);
        return;
      }
    }

    // Update phase progression
    if (phase === 'greeting' && (updated.purpose || updated.startLocation)) {
      setPhase('gathering');
    }

    // Check completeness
    const missing = getMissing(updated);

    // If user also confirmed AND we have everything → generate right away
    if (intent.isConfirm && missing.length === 0 && (phase === 'confirming' || (hadNewInfo && phase !== 'generating'))) {
      setPhase('generating');
      const prefix = acks.length > 0 ? acks.join('. ') + '. ' : '';
      await aiSay(`${prefix}Generating routes tailored to your needs...`, 1000);
      generateRoutes(updated);
      return;
    }

    // If we were confirming and user just rejected (no new info)
    if (phase === 'confirming' && intent.isReject && !hadNewInfo) {
      setPhase('gathering');
      await aiSay("No worries! What would you like to change?", 1200);
      return;
    }

    // If all info complete → move to confirming
    if (missing.length === 0) {
      setPhase('confirming');
      const summary = formatSummary(updated);
      const prefix = acks.length > 0 ? acks.join('. ') + '.\n\n' : '';
      await aiSay(`${prefix}Here's your route plan:\n${summary}\n\nLooks good? Say "yes" to generate routes, or tell me what to change.`, 1800);
    } else {
      // Ask for next missing piece naturally
      let nextQ = '';
      if (missing.includes('purpose')) {
        nextQ = "What kind of route are you after?";
      } else if (missing.includes('start')) {
        nextQ = "Where would you like to start from?";
      } else if (missing.includes('destination')) {
        nextQ = "And where are you heading? Or say 'loop' for a round trip.";
      }

      const full = acks.length > 0
        ? `${acks.join('. ')}.\n\n${nextQ}`
        : nextQ;

      await aiSay(full, acks.length > 0 ? 1500 : 1000);
    }
  }, [prefs, phase, isTyping, addMsg, aiSay, geocode, getGPS, routeOptions, askAI, pendingRouteData, acceptPendingRoute, handleStartNavigation]);

  // ── Purpose card click ──

  const handlePurposeClick = useCallback(async (label: string, internalPurpose: string) => {
    if (isTyping) return;
    addMsg('user', label);
    const updated = { ...prefs, purpose: internalPurpose, purposeRaw: label };
    setPrefs(updated);
    setPhase('gathering');

    // Respond naturally using the card label
    const responses = [
      `${label} — sounds great!\n\nWhere would you like to start from?`,
      `Nice, ${label.toLowerCase()}! Where are you starting from?`,
      `${label} it is!\n\nWhere would you like to start?`,
    ];
    await aiSay(responses[Math.floor(Math.random() * responses.length)], 1200);
  }, [prefs, isTyping, addMsg, aiSay]);

  // ── GPS button click ──

  const handleGPSClick = useCallback(async (locationType: 'start' | 'destination') => {
    if (isTyping) return;
    addMsg('user', 'Use my current location');
    setIsTyping(true);
    setIsLoadingGPS(true);

    const coords = await getGPS();
    setIsLoadingGPS(false);

    if (coords === 'denied') {
      setIsTyping(false);
      await aiSay(`Location access is blocked.\n\niOS: Settings → Privacy & Security → Location Services → Safari → While Using\n\nAndroid: Settings → Site Settings → Location → Allow blurpie.com\n\nOr just type your starting address.`, 1200);
      return;
    }

    if (!coords) {
      setIsTyping(false);
      await aiSay(`Couldn't get your location. Try tapping the button again, or type your starting address.`, 1200);
      return;
    }

    const locName = `Current location (${coords.lat.toFixed(4)}, ${coords.lng.toFixed(4)})`;
    const updated = { ...prefs };

    setIsTyping(false);

    if (locationType === 'start') {
      updated.startLocation = { name: locName, coords };
      setPrefs(updated);

      const missing = getMissing(updated);
      if (missing.length === 0) {
        setPhase('confirming');
        const summary = formatSummary(updated);
        await aiSay(`Got your location!\n\nHere's your route plan:\n${summary}\n\nReady to generate routes?`, 1500);
      } else {
        await aiSay("Got your location! And where are you heading? Or say 'loop' for a round trip.", 1200);
      }
    } else {
      updated.destination = { name: locName, coords };
      setPrefs(updated);

      // Check if same as start → loop
      if (updated.startLocation?.coords) {
        const d = Math.sqrt(
          Math.pow(updated.startLocation.coords.lat - coords.lat, 2) +
          Math.pow(updated.startLocation.coords.lng - coords.lng, 2)
        );
        if (d < 0.001) {
          await aiSay("Same as your starting point — so a loop route! How far would you like to walk? (e.g., 3km, 5km)", 1500);
          return;
        }
      }

      const missing = getMissing(updated);
      if (missing.length === 0) {
        setPhase('confirming');
        const summary = formatSummary(updated);
        await aiSay(`Here's your route plan:\n${summary}\n\nReady to generate routes?`, 1500);
      }
    }
  }, [prefs, isTyping, addMsg, aiSay, getGPS]);

  // ── Loop button click ──

  const handleLoopClick = useCallback(async () => {
    if (isTyping || !prefs.startLocation) return;
    addMsg('user', 'Loop route');
    const updated = {
      ...prefs,
      destination: { name: 'Back to start (loop)', coords: prefs.startLocation.coords },
    };
    setPrefs(updated);
    await aiSay("A loop route! How far would you like to walk? Pick a distance or type one (e.g., 5km).", 1200);
  }, [prefs, isTyping, addMsg, aiSay]);

  // ── Loop distance click ──

  const handleLoopDistanceClick = useCallback(async (km: number) => {
    if (isTyping) return;
    addMsg('user', `${km} km loop`);
    const updated = { ...prefs, loopDistance: km };
    setPrefs(updated);

    const missing = getMissing(updated);
    if (missing.length === 0) {
      setPhase('confirming');
      const summary = formatSummary(updated);
      await aiSay(`Here's your route plan:\n${summary}\n\nLooks good? Say "yes" to generate routes!`, 1500);
    }
  }, [prefs, isTyping, addMsg, aiSay]);

  // ── Route generation ──

  const generateRoutes = useCallback((currentPrefs: RoutePrefs) => {
    setIsTyping(true);
    setThinkingSteps([]);
    setCachedMapboxData(null);

    // Build contextual thinking steps based on user preferences
    const p = currentPrefs.purpose;
    const steps: string[] = ['Understanding your preferences...'];

    // Step 2: purpose-based
    if (p.includes('Exercise') || p.includes('Running')) {
      steps.push('Mapping running trails nearby...');
    } else if (p.includes('Commute')) {
      steps.push('Finding efficient walking paths...');
    } else if (p.includes('Exploring') || p.includes('Sightseeing')) {
      steps.push('Discovering interesting areas nearby...');
    } else {
      steps.push('Scanning walkable paths in the area...');
    }

    // Step 3: preference-based
    const prefSteps: string[] = [];
    if (currentPrefs.preferences.some(x => x.includes('air'))) prefSteps.push('Checking real-time air quality data...');
    if (currentPrefs.preferences.some(x => x.includes('shade'))) prefSteps.push('Identifying shaded pathways...');
    if (currentPrefs.preferences.some(x => x.includes('quiet'))) prefSteps.push('Analyzing noise levels on routes...');
    if (currentPrefs.preferences.some(x => x.includes('scenic'))) prefSteps.push('Finding scenic viewpoints...');
    if (currentPrefs.preferences.some(x => x.includes('cafe'))) prefSteps.push('Locating cafes and rest stops...');
    if (currentPrefs.preferences.some(x => x.includes('well-lit'))) prefSteps.push('Checking path lighting conditions...');
    if (currentPrefs.preferences.some(x => x.includes('flat'))) prefSteps.push('Evaluating terrain elevation...');
    if (currentPrefs.preferences.some(x => x.includes('park'))) prefSteps.push('Finding nearby parks and green spaces...');
    steps.push(prefSteps.length > 0 ? prefSteps[0] : 'Evaluating environmental conditions...');

    steps.push('Comparing route options...');
    steps.push('Optimizing for your priorities...');

    // Progressively reveal steps
    const STEP_DELAY = 800;
    steps.forEach((step, i) => {
      setTimeout(() => {
        setThinkingSteps(prev => [...prev, step]);
      }, i * STEP_DELAY);
    });

    // After all steps shown, call Mapbox to get REAL routes, then generate cards
    setTimeout(async () => {
      const token = import.meta.env.VITE_MAPBOX_TOKEN || 'pk.eyJ1Ijoid2VubWlueWFwIiwiYSI6ImNta3g3OThuazA2ejAzZHF1M3d0eWZudnEifQ.LXK4nArE7sTpjCtTlwdFMw';

      // Helper to generate loop waypoints at a given bearing offset (degrees)
      const makeLoopWaypoints = (start: { lat: number; lng: number }, distKm: number, bearingOffset: number) => {
        const radiusKm = distKm / 7.5;
        const radiusDeg = radiusKm / 111;
        const lngFactor = 1 / Math.cos(start.lat * Math.PI / 180);
        const toRad = (d: number) => d * Math.PI / 180;
        // 3 waypoints at 120° apart, rotated by bearingOffset
        const angles = [0, 120, 240].map(a => toRad(a + bearingOffset));
        const waypoints = angles.map(a => ({
          lng: start.lng + radiusDeg * lngFactor * Math.sin(a),
          lat: start.lat + radiusDeg * Math.cos(a),
        }));
        return `${start.lng},${start.lat};${waypoints.map(w => `${w.lng},${w.lat}`).join(';')};${start.lng},${start.lat}`;
      };

      // Helper to fetch a single route from Mapbox
      const fetchRoute = async (waypointStr: string) => {
        try {
          const resp = await fetch(
            `https://api.mapbox.com/directions/v5/mapbox/walking/${waypointStr}?access_token=${token}&geometries=geojson&overview=full&steps=true`
          );
          const data = await resp.json();
          if (resp.ok && data.code === 'Ok' && data.routes?.length) {
            return data;
          }
        } catch { /* ignore */ }
        return null;
      };

      const isRunning = p.includes('Exercise') || p.includes('Running');
      const paceMinPerKm = isRunning ? 7 : 12;

      const mkDist = (km: number) => `${km.toFixed(1)} km`;
      const mkDur = (durationSec: number) => {
        const mins = Math.round(durationSec / 60);
        return mins >= 60 ? `${Math.floor(mins / 60)} hr ${mins % 60} min` : `${mins} min`;
      };

      // Fetch 3 genuinely different routes in parallel
      const routeResults: { data: any; bearing: number }[] = [];

      if (currentPrefs.startLocation?.coords) {
        const start = currentPrefs.startLocation.coords;

        if (currentPrefs.loopDistance > 0) {
          // Loop routes: 3 different directions (North, SE, SW)
          const bearings = [0, 120, 240];
          const promises = bearings.map(async (bearing) => {
            const waypointStr = makeLoopWaypoints(start, currentPrefs.loopDistance, bearing);
            const data = await fetchRoute(waypointStr);
            if (data) routeResults.push({ data, bearing });
          });
          await Promise.all(promises);
        } else if (currentPrefs.destination?.coords) {
          // Point-to-point: use alternatives=true to get multiple routes
          const end = currentPrefs.destination.coords;
          const waypointStr = `${start.lng},${start.lat};${end.lng},${end.lat}`;
          try {
            const resp = await fetch(
              `https://api.mapbox.com/directions/v5/mapbox/walking/${waypointStr}?access_token=${token}&geometries=geojson&overview=full&steps=true&alternatives=true`
            );
            const data = await resp.json();
            if (resp.ok && data.code === 'Ok' && data.routes?.length) {
              data.routes.forEach((route: any, i: number) => {
                routeResults.push({ data: { ...data, routes: [route] }, bearing: i * 120 });
              });
            }
          } catch { /* ignore */ }

          // If Mapbox only returned 1 route, generate alternatives via waypoint detours
          if (routeResults.length < 2) {
            const midLat = (start.lat + end.lat) / 2;
            const midLng = (start.lng + end.lng) / 2;
            const offsetDeg = 0.003; // ~300m offset for variety
            const lngFactor = 1 / Math.cos(midLat * Math.PI / 180);

            const detours = [
              { lat: midLat + offsetDeg, lng: midLng + offsetDeg * lngFactor },
              { lat: midLat - offsetDeg, lng: midLng - offsetDeg * lngFactor },
            ];

            const detourPromises = detours.map(async (wp, i) => {
              const wpStr = `${start.lng},${start.lat};${wp.lng},${wp.lat};${end.lng},${end.lat}`;
              const data = await fetchRoute(wpStr);
              if (data) routeResults.push({ data, bearing: (i + 1) * 120 });
            });
            await Promise.all(detourPromises);
          }
        }
      }

      // Sort routes: shortest first for commute, longest first for running
      routeResults.sort((a, b) => {
        const distA = a.data.routes[0].distance;
        const distB = b.data.routes[0].distance;
        return p.includes('Commute') ? distA - distB : distB - distA;
      });

      // Cache ALL route mapbox data for navigation (selected later)
      if (routeResults.length > 0) {
        const allData = routeResults.map(r => r.data);
        setCachedMapboxData(routeResults[0].data);
        setCachedAllRoutes(allData);
        cachedAllRoutesRef.current = allData; // ref backup for stale closures
      }

      // Analyze all routes in parallel (AQI, elevation, road types)
      const top3 = routeResults.slice(0, 3);
      const metricsPromises = top3.map((result) =>
        analyzeRoute(result.data, result.bearing, isRunning, paceMinPerKm).catch(() => undefined)
      );
      const allMetrics = await Promise.all(metricsPromises);

      // Build route options with real metrics — filter out zero-distance/invalid routes
      const routes: RouteOption[] = top3
        .map((result, i) => {
          const route = result.data.routes[0];
          const distKm = route.distance / 1000;
          const durationSec = isRunning
            ? distKm * paceMinPerKm * 60
            : route.duration;
          const metrics = allMetrics[i];
          const dirLabel = metrics?.direction ? ` (${metrics.direction})` : '';
          return {
            id: String.fromCharCode(97 + i),
            name: `Route ${String.fromCharCode(65 + i)}${dirLabel}`,
            distance: mkDist(distKm),
            duration: mkDur(durationSec),
            distanceKm: distKm,
            durationSec,
            metrics,
            _coords: (route.geometry?.coordinates || []) as [number, number][],
          };
        })
        .filter(r => r.distanceKm > 0.1) // drop zero/near-zero distance routes
        .map((r, i) => ({           // re-letter A, B, C after filtering
          ...r,
          id: String.fromCharCode(97 + i),
          name: r.name.replace(/^Route [A-Z]/, `Route ${String.fromCharCode(65 + i)}`),
        }));

      // ── Badge + elevation sort ──────────────────────────────
      // Re-sort by elevation gain if user wants flat terrain (must happen AFTER metrics are known)
      const wantsFlat = currentPrefs.preferences.some(p => p.includes('flat'));
      if (wantsFlat && routes.length > 1) {
        routes.sort((a, b) => (a.metrics?.elevationGain ?? 9999) - (b.metrics?.elevationGain ?? 9999));
        routes.forEach((r, i) => {
          r.id = String.fromCharCode(97 + i);
          r.name = r.name.replace(/^Route [A-Z]/, `Route ${String.fromCharCode(65 + i)}`);
        });
      }

      // Assign descriptive badges so users can instantly see what each route excels at
      if (routes.length > 1) {
        const withM = routes.filter(r => r.metrics);
        if (withM.length > 1) {
          const elevGains  = withM.map(r => r.metrics!.elevationGain);
          const aqis       = withM.map(r => r.metrics!.aqi);
          const quiets     = withM.map(r => r.metrics!.quietRoadPct);
          const shades     = withM.map(r => r.metrics!.shadeScore);
          const humidities = withM.map(r => r.metrics!.humidity);
          const minElev  = Math.min(...elevGains);
          const maxElev  = Math.max(...elevGains);
          const minAqi   = Math.min(...aqis);
          const maxAqi   = Math.max(...aqis);
          const maxQuiet = Math.max(...quiets);
          const maxShade = Math.max(...shades);
          const minHum   = Math.min(...humidities);
          const maxHum   = Math.max(...humidities);
          const usedBadges = new Set<string>();
          routes.forEach(r => {
            if (!r.metrics) return;
            if (maxElev - minElev >= 8 && r.metrics.elevationGain === minElev && !usedBadges.has('Flattest')) {
              r.badge = 'Flattest'; usedBadges.add('Flattest');
            } else if (maxElev - minElev >= 8 && r.metrics.elevationGain === maxElev && !usedBadges.has('Most Hills')) {
              r.badge = 'Most Hills'; usedBadges.add('Most Hills');
            } else if (maxAqi - minAqi >= 5 && r.metrics.aqi === minAqi && !usedBadges.has('Cleanest Air')) {
              r.badge = 'Cleanest Air'; usedBadges.add('Cleanest Air');
            } else if (maxShade - Math.min(...shades) >= 10 && r.metrics.shadeScore === maxShade && !usedBadges.has('Shadiest')) {
              r.badge = 'Shadiest'; usedBadges.add('Shadiest');
            } else if (maxHum - minHum >= 3 && r.metrics.humidity === minHum && !usedBadges.has('Less Humid')) {
              r.badge = 'Less Humid'; usedBadges.add('Less Humid');
            } else if (maxHum - minHum >= 3 && r.metrics.humidity === maxHum && !usedBadges.has('More Humid')) {
              r.badge = 'More Humid'; usedBadges.add('More Humid');
            } else if (r.metrics.quietRoadPct === maxQuiet && !usedBadges.has('Quietest')) {
              r.badge = 'Quietest'; usedBadges.add('Quietest');
            }
          });
        }
      }
      // ────────────────────────────────────────────────────────

      // Fallback if no routes fetched
      if (routes.length === 0) {
        const fallbackDist = currentPrefs.loopDistance || 3;
        routes.push({
          id: 'a', name: 'Route A',
          distance: mkDist(fallbackDist),
          duration: mkDur(fallbackDist * paceMinPerKm * 60),
          distanceKm: fallbackDist,
          durationSec: fallbackDist * paceMinPerKm * 60,
        });
      }

      setRouteOptions(routes);

      // Store full coordinates + step data for the inline route map
      const mapData: RouteMapData[] = routes.map((r, i) => ({
        id: r.id,
        name: r.name,
        distance: r.distance,
        duration: r.duration,
        coords: (r._coords || []) as [number, number][],
        metrics: r.metrics,
        steps: top3[i]?.data?.routes?.[0]?.legs?.flatMap((l: any) => l.steps || []) || [],
      }));
      setRouteMapData(mapData);

      setThinkingSteps([]);
      setIsTyping(false);

      setTimeout(() => {
        const requested = currentPrefs.loopDistance;
        if (routeResults.length > 0) {
          const actualDist = routeResults[0].data.routes[0].distance / 1000;
          const mismatch = requested > 0 && (actualDist / requested > 1.3 || actualDist / requested < 0.7);
          if (mismatch) {
            addMsg('ai', `A ${requested}km loop isn't quite available in this area — the closest routes I found range from ${routes[routes.length - 1].distance} to ${routes[0].distance}. Here are your options:`);
          } else {
            const flattest = routes.find(r => r.badge === 'Flattest');
            const cleanest = routes.find(r => r.badge === 'Cleanest Air');
            let hint = '';
            if (flattest) hint = ` ${flattest.name} has the least elevation gain (+${flattest.metrics?.elevationGain ?? 0}m).`;
            else if (cleanest) hint = ` ${cleanest.name} has the cleanest air (AQI ${cleanest.metrics?.aqi ?? 0}).`;
            addMsg('ai', `I've analyzed ${routes.length} routes with real-time air quality, elevation, and road data.${hint} Pick the one that suits you best:`);
          }
        } else {
          addMsg('ai', `I've prepared a route option for you. Take a look:`);
        }
        setPhase('selecting');

        // If user asked for "full report" before routes were ready, auto-show the route map now
        if (showRouteMapAfterGenerateRef.current) {
          showRouteMapAfterGenerateRef.current = false;
          setTimeout(() => {
            addMsg('ai', "Here's the full air quality analysis along each route:");
            addMsg('ai', '[ROUTE_MAP]');
          }, 800);
        }
      }, 400);
    }, steps.length * STEP_DELAY + 600);
  }, [addMsg]);

  // ── Route selection ──

  const handleRouteSelect = useCallback(async (routeId: string) => {
    const route = routeOptions.find(r => r.id === routeId);
    if (!route) return;

    setSelectedRoute(route);
    addMsg('user', route.name);
    addMsg('ai', `Great pick! ${route.name} — ${route.distance}, ~${route.duration}. Opening map...`);
    setPhase('ready');

    try {
      const routeIndex = routeId.charCodeAt(0) - 97; // 'a'=0, 'b'=1, 'c'=2
      // Use ref (never stale) with state as fallback
      const allRoutes = cachedAllRoutesRef.current.length > 0 ? cachedAllRoutesRef.current : cachedAllRoutes;
      const mapboxData = allRoutes[routeIndex] || allRoutes[0];

      if (mapboxData?.routes?.[0] && prefs.startLocation?.coords) {
        setCachedMapboxData(mapboxData);
        const start = prefs.startLocation.coords;
        const end = prefs.destination?.coords || start;
        const mbRoute = mapboxData.routes[0];
        const polyline = mbRoute.geometry.coordinates.map(([lng, lat]: [number, number]) => ({ lat, lng }));
        const steps = mbRoute.legs.flatMap((leg: any) =>
          leg.steps.map((s: any) => ({
            instruction: s.maneuver.instruction,
            distance: Math.round(s.distance),
            duration: Math.round(s.duration),
            maneuver: convertManeuver(s.maneuver.type, s.maneuver.modifier),
            coordinates: { lat: s.maneuver.location[1], lng: s.maneuver.location[0] },
          }))
        );
        const actualKm = mbRoute.distance / 1000;
        const routeData = {
          id: `route_${Date.now()}`,
          priority: 'safest' as const,
          origin: { lat: start.lat, lng: start.lng },
          destination: { lat: end.lat, lng: end.lng },
          destinationName: prefs.loopDistance > 0 ? `${actualKm.toFixed(1)}km Loop` : prefs.destination?.name || 'Destination',
          totalDistance: mbRoute.distance,
          totalDuration: mbRoute.duration,
          steps,
          cognitiveScore: 85,
          arrivalNeuralIndex: 80,
          polyline,
          averageNI: 80,
          exposureScore: 20,
        };
        sessionStorage.setItem('blurp_pending_route', JSON.stringify(routeData));
        sessionStorage.setItem('blurp_preview_mode', 'true');
        await new Promise(r => setTimeout(r, 800));
        navigate('/', { replace: true, state: { previewRoute: true } });
      } else {
        // No cached route data — use handleStartNavigation which fetches fresh
        console.warn('[RouteSelect] No cached mapbox data, falling back to handleStartNavigation');
        await new Promise(r => setTimeout(r, 800));
        handleStartNavigation();
      }
    } catch (err) {
      console.error('[RouteSelect] Error building route:', err);
      // Fallback: try handleStartNavigation which has its own route fetching
      handleStartNavigation();
    }
  }, [routeOptions, cachedAllRoutes, prefs, addMsg, navigate, handleStartNavigation]);

  // ── Initial messages ──

  useEffect(() => {
    if (initialized) return;
    setInitialized(true);

    const init = async () => {
      // Check subscription — if not paid, show paywall and redirect back on dismiss
      useSubscriptionStore.getState().fetchSubscription().then(() => {
        if (!useSubscriptionStore.getState().canAccessFeature('routePlanning')) {
          showUpgradeModal('routePlanning', 'pro', () => {
            // User dismissed without paying — send them back to landing page
            navigate('/', { replace: true });
          });
        }
      });

      if (initialQuery) {
        await aiSay(`Hey! I see you're looking for: "${initialQuery}"`, 1000);

        // Parse the initial query
        const intent = parseIntent(initialQuery);
        const updated: RoutePrefs = { ...prefs };
        const acks: string[] = [];

        if (intent.purpose) {
          updated.purpose = intent.purpose;
          const purposeMatch = initialQuery.match(/\b(run(ning)?|jog(ging)?|exercise|fitness|workout|training|sprint|commut(e|ing)|office|school|getting somewhere|leisure|relax(ing)?|casual|stroll(ing)?|wander(ing)?|unwind|chill(ing)?|explor(e|ing)|sightsee(ing)?|tourist|visit(ing)?|discover(ing)?|adventure|look(ing)? around)\b/i);
          const userWord = purposeMatch ? purposeMatch[0] : friendlyPurpose(intent.purpose);
          updated.purposeRaw = userWord;
          acks.push(`Sounds like you want ${userWord.startsWith('a ') ? userWord : (/^(getting|exploring|jogging|running|training|strolling|wandering|looking)/.test(userWord) ? userWord : `a ${userWord}`)}`);
        }
        if (intent.preferences.length > 0) {
          updated.preferences = intent.preferences;
          acks.push(`with ${intent.preferences.join(' and ')}`);
        }
        if (intent.concerns.length > 0) {
          updated.concerns = intent.concerns;
        }

        if (acks.length > 0) {
          setPrefs(updated);
          setPhase('gathering');
          const missing = getMissing(updated);
          let nextQ = "Where would you like to start from?";
          if (missing.includes('purpose')) nextQ = "What will you use this route for?";

          await aiSay(`${acks.join(', ')}.\n\n${nextQ}`, 1500);
        } else {
          await aiSay("Tell me more about what you're looking for, or pick one of these to get started:", 1200);
        }
      } else {
        await aiSay("Hi! I'll help you find the perfect route.", 1200);
        await aiSay("What kind of route are you looking for? Pick one below, or just tell me in your own words.", 1200);
      }
    };

    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Computed UI state ──

  const showPurposeCards = (phase === 'greeting' || (phase === 'gathering' && !prefs.purpose)) && !isTyping && messages.length >= 2;
  const showLocationBtns = phase === 'gathering' && !isTyping && prefs.purpose && !prefs.startLocation;
  const showDestBtns = phase === 'gathering' && !isTyping && prefs.purpose && prefs.startLocation && !prefs.destination && prefs.loopDistance === 0;
  const showLoopDist = phase === 'gathering' && !isTyping && prefs.destination?.name?.includes('loop') && prefs.loopDistance === 0;
  const showConfirmBtns = phase === 'confirming' && !isTyping;
  const lastAiMsg = messages.filter(m => m.type === 'ai').at(-1)?.content ?? '';
  const hasSpecialCard = lastAiMsg === '[ROUTE_MAP]' || lastAiMsg === '[FORECAST]';
  const showRouteCards = phase === 'selecting' && routeOptions.length > 0 && !isTyping && !hasSpecialCard;
  const showPreviewBtns = phase === 'ready' && !isTyping && !pendingRouteData;
  const showMismatchBtns = phase === 'ready' && !isTyping && !!pendingRouteData;

  const placeholder = (() => {
    if (phase === 'generating') return 'Generating routes...';
    if (phase === 'greeting') return 'Describe the route you want, or pick one below...';
    if (phase === 'confirming') return 'Add preferences, or say "yes" to generate...';
    if (phase === 'selecting') return 'Pick a route or tell me what matters most...';
    if (phase === 'ready') return 'Ready to preview!';
    if (!prefs.purpose) return 'What kind of route are you looking for?';
    if (!prefs.startLocation) return 'Type your starting address...';
    if (!prefs.destination && prefs.loopDistance === 0) return 'Type your destination...';
    return 'Tell me more or add preferences...';
  })();

  // ── Render ──

  return (
    <div className="min-h-screen bg-[#030508] text-white flex flex-col">
      {/* Background */}
      <div className="fixed inset-0">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_80%_at_50%_-20%,rgba(34,197,94,0.15),transparent)]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_60%_at_100%_100%,rgba(6,182,212,0.1),transparent)]" />
      </div>

      {/* Header */}
      <motion.nav
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        className="relative z-10 px-4 py-3 border-b border-white/5 flex-shrink-0"
      >
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            <span className="text-sm">Back</span>
          </button>
          <div className="flex items-center gap-2">
            <img src="/logo.png" alt="Blurp.ie" className="w-8 h-8 rounded-full" />
            <span className="font-semibold text-sm">Blurp.ie Assistant</span>
          </div>
          <div className="w-16" />
        </div>
      </motion.nav>

      {/* Messages */}
      <div className="relative z-10 flex-1 overflow-y-auto">
        <div className="max-w-3xl mx-auto px-4 py-6 space-y-4">
          <AnimatePresence>
            {messages.map((msg) => (
              <motion.div
                key={msg.id}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                {msg.content.startsWith('[FORECAST]') ? (
                  <div className="w-full max-w-[92%]">
                    <ForecastCard />
                    {phase === 'selecting' && (
                      <button onClick={() => addMsg('user', 'back to routes')} className="mt-2 text-[11px] text-slate-500 hover:text-slate-300 transition-colors">
                        ← Back to routes
                      </button>
                    )}
                  </div>
                ) : msg.content === '[ROUTE_MAP]' ? (
                  <div className="w-full max-w-[92%]">
                    <RouteMapCard routes={routeMapData} />
                    {phase === 'selecting' && (
                      <button onClick={() => addMsg('user', 'back to routes')} className="mt-2 text-[11px] text-slate-500 hover:text-slate-300 transition-colors">
                        ← Back to routes
                      </button>
                    )}
                  </div>
                ) : (
                  <div className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                    msg.type === 'ai'
                      ? 'bg-white/10 border border-white/10 text-white'
                      : 'bg-gradient-to-r from-emerald-500 to-cyan-500 text-black font-medium'
                  }`}>
                    <p className="text-sm whitespace-pre-line">{msg.content}</p>
                  </div>
                )}
              </motion.div>
            ))}
          </AnimatePresence>

          {/* Typing indicator */}
          {isTyping && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex justify-start">
              <div className="bg-white/10 border border-white/10 rounded-2xl px-4 py-3">
                <div className="flex items-center gap-1.5">
                  {[0, 0.2, 0.4].map((delay, i) => (
                    <motion.div
                      key={i}
                      animate={{ scale: [1, 1.3, 1] }}
                      transition={{ duration: 0.6, repeat: Infinity, repeatDelay: 0.2, delay }}
                      className="w-2 h-2 rounded-full bg-emerald-400"
                    />
                  ))}
                </div>
              </div>
            </motion.div>
          )}

          {/* Route generation thinking process */}
          {phase === 'generating' && thinkingSteps.length > 0 && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex justify-start">
              <div className="bg-white/[0.06] border border-white/[0.08] rounded-2xl px-4 py-3 max-w-[85%]">
                <div className="flex items-center gap-2 mb-2.5">
                  <div className="w-5 h-5 rounded-full bg-emerald-500/20 flex items-center justify-center">
                    <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                  </div>
                  <span className="text-xs font-medium text-emerald-400/80 uppercase tracking-wider">Thinking</span>
                </div>
                <div className="space-y-1.5 pl-1">
                  {thinkingSteps.map((step, i) => {
                    const isLatest = i === thinkingSteps.length - 1;
                    return (
                      <motion.div
                        key={step}
                        initial={{ opacity: 0, x: -8 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.3, ease: 'easeOut' }}
                        className="flex items-center gap-2"
                      >
                        {isLatest ? (
                          <motion.div
                            animate={{ rotate: 360 }}
                            transition={{ duration: 0.8, repeat: Infinity, ease: 'linear' }}
                            className="w-3.5 h-3.5 border-[1.5px] border-emerald-500/30 border-t-emerald-400 rounded-full flex-shrink-0"
                          />
                        ) : (
                          <svg className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                        <span className={`text-sm ${isLatest ? 'text-gray-300' : 'text-gray-500'}`}>{step}</span>
                      </motion.div>
                    );
                  })}
                </div>
              </div>
            </motion.div>
          )}

          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Input Area */}
      <div className="relative z-10 border-t border-white/5 bg-[#030508]/80 backdrop-blur-lg flex-shrink-0">
        <div className="max-w-3xl mx-auto px-4 py-3">
          <AnimatePresence mode="wait">
            {/* Purpose Cards */}
            {showPurposeCards && (
              <motion.div
                key="purpose"
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                className="mb-3"
              >
                <div className="grid grid-cols-2 gap-3">
                  {purposeOptions.map((opt, i) => (
                    <motion.button
                      key={opt.label}
                      initial={{ opacity: 0, y: 15 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.3 + i * 0.12, duration: 0.5, ease: 'easeOut' }}
                      onClick={() => handlePurposeClick(opt.label, opt.internalPurpose)}
                      className="relative overflow-hidden h-28 rounded-2xl border border-white/[0.08] hover:border-white/25 transition-all duration-300 text-left group hover:scale-[1.03] active:scale-[0.97]"
                    >
                      <img src={opt.image} alt="" className="absolute inset-0 w-full h-full object-cover opacity-40 group-hover:opacity-55 transition-all duration-500 group-hover:scale-110 transform" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/20" />
                      <div className="relative z-10 h-full flex items-end p-4">
                        <span className="text-[13px] font-bold text-white drop-shadow-lg tracking-wide leading-tight">{opt.label}</span>
                      </div>
                    </motion.button>
                  ))}
                </div>
              </motion.div>
            )}

            {/* Location buttons (start) */}
            {showLocationBtns && (
              <motion.div
                key="start-loc"
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                className="mb-3"
              >
                <button
                  onClick={() => handleGPSClick('start')}
                  disabled={isLoadingGPS}
                  className="w-full py-3 px-4 bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-400 hover:to-cyan-400 rounded-xl font-semibold text-black transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {isLoadingGPS ? (
                    <>
                      <motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: 'linear' }} className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full" />
                      Getting location...
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="4" /><line x1="12" y1="2" x2="12" y2="6" /><line x1="12" y1="18" x2="12" y2="22" /><line x1="2" y1="12" x2="6" y2="12" /><line x1="18" y1="12" x2="22" y2="12" /></svg>
                      Use Current Location
                    </>
                  )}
                </button>
              </motion.div>
            )}

            {/* Destination buttons */}
            {showDestBtns && (
              <motion.div
                key="dest-loc"
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                className="mb-3 space-y-2"
              >
                <button
                  onClick={() => handleGPSClick('destination')}
                  disabled={isLoadingGPS}
                  className="w-full py-3 px-4 bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-400 hover:to-cyan-400 rounded-xl font-semibold text-black transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {isLoadingGPS ? (
                    <>
                      <motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: 'linear' }} className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full" />
                      Getting location...
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="4" /><line x1="12" y1="2" x2="12" y2="6" /><line x1="12" y1="18" x2="12" y2="22" /><line x1="2" y1="12" x2="6" y2="12" /><line x1="18" y1="12" x2="22" y2="12" /></svg>
                      Use Current Location
                    </>
                  )}
                </button>
                {prefs.startLocation?.coords && (
                  <button
                    onClick={handleLoopClick}
                    className="w-full py-3 px-4 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-cyan-500/40 rounded-xl font-medium text-gray-300 transition-all flex items-center justify-center gap-2"
                  >
                    <svg className="w-4 h-4 text-cyan-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10" /><polyline points="1 20 1 14 7 14" /><path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15" /></svg>
                    Loop route
                  </button>
                )}
              </motion.div>
            )}

            {/* Loop distance */}
            {showLoopDist && (
              <motion.div
                key="loop-dist"
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                className="mb-3"
              >
                <div className="grid grid-cols-4 gap-2">
                  {[
                    { km: 1, label: '1 km', time: '~12 min' },
                    { km: 3, label: '3 km', time: '~35 min' },
                    { km: 5, label: '5 km', time: '~55 min' },
                    { km: 10, label: '10 km', time: '~1h 50m' },
                  ].map(opt => (
                    <button
                      key={opt.km}
                      onClick={() => handleLoopDistanceClick(opt.km)}
                      className="p-3 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-emerald-500/50 rounded-xl transition-all text-center"
                    >
                      <div className="text-sm font-semibold text-white">{opt.label}</div>
                      <div className="text-[10px] text-gray-400 mt-0.5">{opt.time}</div>
                    </button>
                  ))}
                </div>
              </motion.div>
            )}

            {/* Confirm / Edit buttons */}
            {showConfirmBtns && (
              <motion.div
                key="confirm"
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                className="mb-3 flex gap-2"
              >
                <button
                  onClick={() => {
                    if (!useSubscriptionStore.getState().canAccessFeature('routePlanning')) {
                      showUpgradeModal('routePlanning', 'pro');
                      return;
                    }
                    setPhase('generating');
                    generateRoutes(prefs);
                  }}
                  className="flex-1 py-3 px-4 bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-400 hover:to-cyan-400 rounded-xl font-semibold text-black transition-all"
                >
                  Generate Routes
                </button>
                <button
                  onClick={() => handleSend('I want to change something')}
                  className="py-3 px-4 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl text-gray-400 hover:text-white transition-all text-sm"
                >
                  Edit
                </button>
              </motion.div>
            )}

            {/* Route option cards */}
            {showRouteCards && (
              <motion.div
                key="routes"
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                className="mb-3 space-y-2"
              >
                {routeOptions.map(route => {
                  const isRecommended = route.id === recommendedRouteId;
                  return (
                  <button
                    key={route.id}
                    onClick={() => handleRouteSelect(route.id)}
                    className={`w-full rounded-2xl transition-all text-left group overflow-hidden ${
                      isRecommended
                        ? 'bg-blue-950/60 border border-blue-500/60 hover:border-blue-400/80 shadow-[0_0_0_1px_rgba(59,130,246,0.2)]'
                        : 'bg-slate-800/80 hover:bg-slate-700/80 border border-slate-700/60 hover:border-emerald-500/40'
                    }`}
                  >
                    {/* Header bar */}
                    <div className="flex items-center justify-between px-4 pt-3.5 pb-2">
                      <div className="flex items-center gap-2">
                        <h3 className={`font-semibold text-[15px] tracking-tight transition-colors ${isRecommended ? 'text-blue-300' : 'text-white group-hover:text-emerald-400'}`}>{route.name}</h3>
                        {isRecommended && (
                          <span className="text-[9px] font-semibold uppercase tracking-widest text-blue-400 bg-blue-500/15 border border-blue-500/30 rounded-full px-2 py-0.5">Recommended</span>
                        )}
                        {!isRecommended && route.badge && (
                          <span className="text-[9px] font-semibold uppercase tracking-widest text-emerald-400 bg-emerald-500/15 border border-emerald-500/30 rounded-full px-2 py-0.5">{route.badge}</span>
                        )}
                      </div>
                      <div className="flex items-center gap-1.5 text-[13px] text-gray-400 font-medium tabular-nums">
                        <span>{route.distance}</span>
                        <span className="text-gray-600">|</span>
                        <span>{route.duration}</span>
                      </div>
                    </div>

                    {route.metrics ? (
                      <>
                        {/* Air quality verdict banner */}
                        <div
                          className="mx-3 mb-2 mt-1 rounded-xl px-3 py-2.5 flex items-center justify-between"
                          style={{ background: route.metrics.exposureBg, border: `1px solid ${route.metrics.exposureColor}30` }}
                        >
                          <div className="flex items-center gap-2">
                            {/* Lungs icon */}
                            <svg className="w-4 h-4 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ color: route.metrics.exposureColor }}>
                              <path d="M12 4c0 0 0 3-3 5s-6 2-6 7c0 2.5 2 4 4 4s4-2 4-4V4z" />
                              <path d="M12 4c0 0 0 3 3 5s6 2 6 7c0 2.5-2 4-4 4s-4-2-4-4V4z" />
                            </svg>
                            <div>
                              <p className="text-[11px] font-semibold leading-tight" style={{ color: route.metrics.exposureColor }}>{route.metrics.exposureLabel}</p>
                              <p className="text-[10px] text-slate-500 leading-tight">{route.metrics.exposureSublabel}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-[13px] font-semibold tabular-nums" style={{ color: route.metrics.exposureColor }}>~{route.metrics.breathingExposureMcg} µg</p>
                            <p className="text-[9px] text-slate-600 uppercase tracking-wide">PM2.5 inhaled</p>
                          </div>
                        </div>

                        {/* Divider */}
                        <div className="mx-4 h-px bg-slate-700/50" />

                        {/* Metrics — row 1: AQI / Elevation / Humidity */}
                        <div className="grid grid-cols-3 gap-0 px-1 pt-2.5 pb-1">
                          {/* AQI */}
                          <div className="flex flex-col items-center gap-1">
                            <svg className="w-[18px] h-[18px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                              <circle cx="12" cy="12" r="10" className="text-cyan-500/60" />
                              <circle cx="12" cy="12" r="3" style={{ fill: route.metrics.aqiColor, stroke: 'none' }} />
                            </svg>
                            <span className="text-[11px] font-semibold text-white tabular-nums">{route.metrics.aqi}</span>
                            <span className="text-[9px] uppercase tracking-wider font-medium" style={{ color: route.metrics.aqiColor }}>{route.metrics.aqiLabel}</span>
                          </div>

                          {/* Elevation */}
                          <div className="flex flex-col items-center gap-1">
                            <svg className="w-[18px] h-[18px] text-cyan-500/60" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M3 20l5.5-8.5L12 16l4-7 5 11H3z" />
                            </svg>
                            <span className="text-[11px] font-semibold text-white tabular-nums">+{route.metrics.elevationGain}m</span>
                            <span className="text-[9px] text-cyan-600/80 uppercase tracking-wider font-medium">Elev gain</span>
                          </div>

                          {/* Humidity */}
                          <div className="flex flex-col items-center gap-1">
                            <svg className="w-[18px] h-[18px] text-cyan-500/60" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M12 2C6.5 11 4 15 4 17a8 8 0 0 0 16 0c0-2-2.5-6-8-15z" />
                            </svg>
                            <span className="text-[11px] font-semibold text-white tabular-nums">{route.metrics.humidity}%</span>
                            <span className="text-[9px] text-cyan-600/80 uppercase tracking-wider font-medium">Humidity</span>
                          </div>
                        </div>

                        {/* Metrics — row 2: Shade / Quiet / Pace */}
                        <div className="grid grid-cols-3 gap-0 px-1 pb-2.5 pt-1">
                          {/* Shade */}
                          <div className="flex flex-col items-center gap-1">
                            <svg className="w-[18px] h-[18px] text-cyan-500/60" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                              <circle cx="12" cy="12" r="4" />
                              <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
                              <path d="M7 12a5 5 0 0 1 5-5" strokeOpacity="0.4" />
                            </svg>
                            <span className="text-[11px] font-semibold text-white tabular-nums">{route.metrics.shadeScore}</span>
                            <span className="text-[9px] text-cyan-600/80 uppercase tracking-wider font-medium">Shade</span>
                          </div>

                          {/* Quiet roads */}
                          <div className="flex flex-col items-center gap-1">
                            <svg className="w-[18px] h-[18px] text-cyan-500/60" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M17.5 19H9a7 7 0 1 1 6.71-9h1.79a4.5 4.5 0 1 1 0 9z" />
                            </svg>
                            <span className="text-[11px] font-semibold text-white tabular-nums">{route.metrics.quietRoadPct}%</span>
                            <span className="text-[9px] text-cyan-600/80 uppercase tracking-wider font-medium">Quiet</span>
                          </div>

                          {/* Pace */}
                          <div className="flex flex-col items-center gap-1">
                            <svg className="w-[18px] h-[18px] text-cyan-500/60" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                              <circle cx="12" cy="12" r="10" />
                              <polyline points="12 6 12 12 16 14" />
                            </svg>
                            <span className="text-[11px] font-semibold text-white tabular-nums">{route.metrics.pacePerKm}</span>
                            <span className="text-[9px] text-cyan-600/80 uppercase tracking-wider font-medium">Pace</span>
                          </div>
                        </div>

                        {/* Bottom tag */}
                        <div className="px-4 pb-3 pt-0.5">
                          <span className="inline-block text-[10px] text-cyan-400/60 bg-cyan-900/20 border border-cyan-800/30 rounded-full px-2.5 py-0.5 font-medium tracking-wide">
                            {route.metrics.roadBreakdown}
                          </span>
                        </div>
                      </>
                    ) : (
                      <div className="px-4 pb-3 pt-1">
                        <div className="flex items-center gap-2 text-xs text-gray-500">
                          <div className="w-3 h-3 border-2 border-gray-600 border-t-gray-400 rounded-full animate-spin" />
                          Analyzing route...
                        </div>
                      </div>
                    )}
                  </button>
                  );
                })}
              </motion.div>
            )}

            {/* Distance mismatch – accept or adjust */}
            {showMismatchBtns && (
              <motion.div
                key="mismatch"
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                className="mb-3 space-y-2"
              >
                <button
                  onClick={() => acceptPendingRoute()}
                  className="w-full py-4 px-4 bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-400 hover:to-cyan-400 rounded-xl font-bold text-black text-lg transition-all flex items-center justify-center gap-3 active:scale-[0.98]"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                  Yes, go with this
                </button>
                <button
                  onClick={() => {
                    setPendingRouteData(null);
                    setPhase('gathering');
                    addMsg('ai', "No problem! Tell me a different distance, or say 'loop' with a new distance.");
                  }}
                  className="w-full py-3 px-4 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl text-gray-400 hover:text-white transition-all text-sm"
                >
                  Adjust distance
                </button>
              </motion.div>
            )}

            {/* Preview Route button */}
            {showPreviewBtns && (
              <motion.div
                key="preview"
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                className="mb-3 space-y-2"
              >
                <button
                  onClick={handleStartNavigation}
                  className="relative w-full py-4 px-4 bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-400 hover:to-cyan-400 rounded-xl font-bold text-black text-lg transition-all flex items-center justify-center gap-3 overflow-hidden group active:scale-[0.98]"
                >
                  <span className="absolute inset-0 bg-white/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  <svg className="w-6 h-6 relative z-10" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                  </svg>
                  <span className="relative z-10">Preview Route</span>
                  <svg className="w-5 h-5 relative z-10 group-hover:translate-x-0.5 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </button>
                <button
                  onClick={() => {
                    setPhase('selecting');
                    addMsg('ai', 'Here are the other options:');
                  }}
                  className="w-full py-3 px-4 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl text-gray-400 hover:text-white transition-all text-sm"
                >
                  Choose a different route
                </button>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Always-visible input */}
          <div className="flex items-center gap-2">
            <input
              ref={inputRef}
              type="text"
              value={userInput}
              onChange={(e) => setUserInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && userInput.trim()) {
                  handleSend(userInput);
                }
              }}
              placeholder={placeholder}
              disabled={phase === 'generating'}
              className="flex-1 py-3 px-4 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 outline-none focus:border-emerald-500/50 transition-colors disabled:opacity-40"
            />
            <button
              onClick={() => handleSend(userInput)}
              disabled={!userInput.trim() || isTyping || phase === 'generating'}
              className="p-3 rounded-xl bg-emerald-500 hover:bg-emerald-400 disabled:opacity-20 disabled:hover:bg-emerald-500 transition-all flex-shrink-0"
            >
              <svg className="w-5 h-5 text-black" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 12L3.269 3.126A59.768 59.768 0 0121.485 12 59.77 59.77 0 013.27 20.876L5.999 12zm0 0h7.5" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
